
import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { AppState, ChildProfile, Story, LanguageCode, VoiceType, UserAccount, SubscriptionPlan } from './types';
import { 
  TRANSLATIONS, LANGUAGES, COZY_SLEEPING_MOON, VOICES, 
  IconHome, IconUser, IconSettings, IconBack, IconBook, IconDice, IconMic, IconCamera, IconPause, IconPlay, IconStar,
  IconMagicWand, IconTrash
} from './constants';
import StarryBackground from './components/StarryBackground';
import LumiMessage from './components/LumiMessage';
import WeavingCompanion from './components/WeavingCompanion';
import AdminDashboard from './components/AdminDashboard';
import { AuthProvider, useAuth } from './AuthContext';
import { SubscriptionProvider, useSubscription } from './SubscriptionContext';
import { 
  generateStoryBook, generatePageSpeech, 
  generateRandomPrompt, prefetchPageSpeech
} from './services/geminiService';
import { DBService } from './services/dbService';
import { getSavedPhotoReference, optimisePhotoForStory, removeSavedPhotoReference, savePhotoReference } from './services/photoReference';

const MemoizedMoon = memo(() => COZY_SLEEPING_MOON);

const IconRotateDevice = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className={className} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="7" y="3" width="10" height="18" rx="2" />
    <path d="M4.5 8.5A8.5 8.5 0 0 1 10 2" />
    <path d="m4 4 .5 4.5L9 7" />
  </svg>
);

const IconPictureMode = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <circle cx="8.5" cy="9" r="1.2" />
    <path d="m4.5 17 5-5 3.5 3 2.25-2.25L20 17" />
  </svg>
);

const LANGUAGE_LOCALES: Record<LanguageCode, string> = {
  de: 'de-DE', en: 'en-US', fr: 'fr-FR', es: 'es-ES', zh: 'zh-CN', ja: 'ja-JP', it: 'it-IT', ar: 'ar-SA', hi: 'hi-IN',
};

const VOICE_INTROS: Record<LanguageCode, string> = {
  de: 'Hallo, ich bin', en: 'Hello, I am', fr: 'Bonjour, je suis', es: 'Hola, soy', zh: '你好，我是', ja: 'こんにちは、私は', it: 'Ciao, sono', ar: 'مرحبًا، أنا', hi: 'नमस्ते, मैं हूँ',
};

const SUPPORTED_LANGUAGE_CODES = new Set<LanguageCode>(['de', 'en', 'fr', 'es', 'zh', 'ja', 'it', 'ar', 'hi']);

const MOBILE_READER_COPY: Record<LanguageCode, { title: string; description: string }> = {
  de: { title: 'Für das große Bilderbuch: Handy drehen', description: 'Hochkant bleibt jedes Bild vollständig sichtbar.' },
  en: { title: 'For the big picture book: turn your phone', description: 'Every picture stays fully visible in portrait, too.' },
  fr: { title: 'Pour le grand livre illustré : tournez le téléphone', description: 'Chaque image reste aussi entièrement visible en portrait.' },
  es: { title: 'Para el gran libro ilustrado: gira el teléfono', description: 'Cada imagen también se ve completa en vertical.' },
  zh: { title: '想看大幅绘本：请横向旋转手机', description: '竖屏时，每幅图也会完整显示。' },
  ja: { title: '大きな絵本で見るには、スマートフォンを横向きに', description: '縦向きでも、すべての絵が見切れずに表示されます。' },
  it: { title: 'Per il grande libro illustrato: ruota il telefono', description: 'Anche in verticale ogni immagine resta completamente visibile.' },
  ar: { title: 'للاستمتاع بالكتاب الكبير: أدر الهاتف', description: 'تظل كل صورة ظاهرة بالكامل حتى في الوضع العمودي.' },
  hi: { title: 'बड़ी चित्र-पुस्तक के लिए: फ़ोन घुमाएँ', description: 'सीधे फ़ोन में भी हर चित्र पूरा दिखाई देगा।' },
};

const READER_FOCUS_COPY: Record<LanguageCode, { show: string; hide: string; tip: string }> = {
  de: { show: 'Ganzes Bild zeigen', hide: 'Text und Bedienung zeigen', tip: 'Tipp: Tippe oben rechts auf das Bild-Symbol. So kannst du Text und Steuerung kurz ausblenden und die Illustration ganz ansehen.' },
  en: { show: 'Show the full picture', hide: 'Show text and controls', tip: 'Tip: tap the picture icon at the top right to briefly hide the text and controls and see the whole illustration.' },
  fr: { show: 'Afficher toute l’image', hide: 'Afficher le texte et les commandes', tip: 'Astuce : touchez l’icône d’image en haut à droite pour masquer brièvement le texte et les commandes.' },
  es: { show: 'Ver la imagen completa', hide: 'Mostrar texto y controles', tip: 'Consejo: toca el icono de imagen arriba a la derecha para ocultar brevemente el texto y los controles.' },
  zh: { show: '查看完整图片', hide: '显示文字和控制', tip: '提示：轻点右上角的图片图标，可暂时隐藏文字和控制，完整查看插图。' },
  ja: { show: '絵全体を見る', hide: '文章と操作を表示', tip: 'ヒント：右上の絵アイコンをタップすると、文章と操作を一時的に隠して絵全体を見られます。' },
  it: { show: 'Mostra tutta l’immagine', hide: 'Mostra testo e comandi', tip: 'Suggerimento: tocca l’icona dell’immagine in alto a destra per nascondere temporaneamente testo e comandi.' },
  ar: { show: 'إظهار الصورة كاملة', hide: 'إظهار النص وأدوات التحكم', tip: 'نصيحة: المس رمز الصورة أعلى اليمين لإخفاء النص وأدوات التحكم مؤقتًا ورؤية الرسم كاملًا.' },
  hi: { show: 'पूरा चित्र देखें', hide: 'पाठ और नियंत्रण दिखाएँ', tip: 'सुझाव: चित्र को पूरा देखने के लिए ऊपर दाईं ओर चित्र आइकन दबाएँ; पाठ और नियंत्रण थोड़ी देर छिप जाएंगे।' },
};

type LandingPreviewCopy = {
  eyebrow: string;
  heading: string;
  description: string;
  familyMode: string;
  previewHeading: string;
  previewDescription: string;
  steps: [string, string, string];
  wish: string;
  photo: string;
  book: string;
  start: string;
  privacy: string;
};

// The entry screen deliberately uses neutral product previews instead of real
// family uploads. It shows the complete Lumi journey before sign-up without
// ever exposing a child's photo in public marketing.
const LANDING_PREVIEW_COPY: Record<LanguageCode, LandingPreviewCopy> = {
  de: {
    eyebrow: 'Deine Geschichte. Deine Familie. Deine Magie.',
    heading: 'Jede Gute-Nacht-Idee wird zu eurem eigenen Bilderbuch.',
    description: 'Wunsch eingeben, auf Wunsch ein Foto als Vorlage nutzen und gemeinsam zuhören. Sicher, altersgerecht und nur für Familien.',
    familyMode: 'Familienmodus',
    previewHeading: 'So entsteht euer Traum',
    previewDescription: 'Ein kleiner Blick in Lumi – bevor ihr euch registriert.',
    steps: ['Wunsch erzählen', 'Figur & Stimme wählen', 'Gemeinsam lesen'],
    wish: 'Ein Abenteuer im Wolkenwald …',
    photo: 'Dein Foto bleibt privat',
    book: 'Euer digitales Bilderbuch',
    start: 'Geschichtenwelt öffnen',
    privacy: 'Kinderfotos werden nicht öffentlich gezeigt oder in der Bibliothek gespeichert.',
  },
  en: {
    eyebrow: 'Your story. Your family. Your magic.',
    heading: 'Every bedtime idea becomes your own picture book.',
    description: 'Describe a wish, optionally use a photo as a reference, and listen together. Safe, age-appropriate and made for families.',
    familyMode: 'Family mode',
    previewHeading: 'How your dream is made',
    previewDescription: 'A small look inside Lumi before you sign up.',
    steps: ['Tell a wish', 'Choose character & voice', 'Read together'],
    wish: 'An adventure in the cloud forest …',
    photo: 'Your photo stays private',
    book: 'Your digital picture book',
    start: 'Open the story world',
    privacy: 'Children’s photos are never shown publicly or stored in the library.',
  },
  fr: {
    eyebrow: 'Votre histoire. Votre famille. Votre magie.',
    heading: 'Chaque idée du soir devient votre propre livre illustré.',
    description: 'Décrivez un souhait, utilisez une photo comme référence si vous le souhaitez, puis écoutez ensemble. Sûr et adapté à l’âge.',
    familyMode: 'Mode famille',
    previewHeading: 'Comment naît votre rêve',
    previewDescription: 'Un aperçu de Lumi avant l’inscription.',
    steps: ['Raconter un souhait', 'Choisir le héros et la voix', 'Lire ensemble'],
    wish: 'Une aventure dans la forêt des nuages …',
    photo: 'Votre photo reste privée',
    book: 'Votre livre illustré numérique',
    start: 'Ouvrir le monde des histoires',
    privacy: 'Les photos d’enfants ne sont jamais publiques ni conservées dans la bibliothèque.',
  },
  es: {
    eyebrow: 'Tu historia. Tu familia. Tu magia.',
    heading: 'Cada idea para dormir se convierte en vuestro propio libro ilustrado.',
    description: 'Describe un deseo, usa una foto como referencia si quieres y escuchad juntos. Seguro y adecuado para cada edad.',
    familyMode: 'Modo familiar',
    previewHeading: 'Así nace vuestro sueño',
    previewDescription: 'Una pequeña mirada a Lumi antes de registrarte.',
    steps: ['Contar un deseo', 'Elegir personaje y voz', 'Leer juntos'],
    wish: 'Una aventura en el bosque de nubes …',
    photo: 'Tu foto permanece privada',
    book: 'Vuestro libro ilustrado digital',
    start: 'Abrir el mundo de historias',
    privacy: 'Las fotos de niños nunca se muestran públicamente ni se guardan en la biblioteca.',
  },
  zh: {
    eyebrow: '你的故事。你的家人。你的魔法。',
    heading: '每一个睡前想法都会成为你们自己的绘本。',
    description: '说出愿望；如果愿意可用照片作参考；再一起聆听。安全、适龄、专为家庭而设。',
    familyMode: '家庭模式',
    previewHeading: '梦想如何诞生',
    previewDescription: '注册前先看看 Lumi。',
    steps: ['说出愿望', '选择角色与声音', '一起阅读'],
    wish: '云朵森林里的冒险 …',
    photo: '你的照片保持私密',
    book: '你们的数字绘本',
    start: '打开故事世界',
    privacy: '儿童照片绝不会公开展示，也不会保存在图书馆中。',
  },
  ja: {
    eyebrow: 'あなたの物語。あなたの家族。あなたの魔法。',
    heading: '眠る前のアイデアが、家族だけの絵本になります。',
    description: '願いを伝え、必要なら写真を参考にして、一緒に聞きましょう。安全で年齢に合った家族のための体験です。',
    familyMode: 'ファミリーモード',
    previewHeading: '夢ができるまで',
    previewDescription: '登録前に Lumi を少しだけ見てみましょう。',
    steps: ['願いを伝える', '主人公と声を選ぶ', '一緒に読む'],
    wish: '雲の森での冒険 …',
    photo: '写真は非公開のまま',
    book: 'あなたのデジタル絵本',
    start: '物語の世界を開く',
    privacy: 'お子さまの写真が公開表示されたり、本棚に保存されたりすることはありません。',
  },
  it: {
    eyebrow: 'La tua storia. La tua famiglia. La tua magia.',
    heading: 'Ogni idea della buonanotte diventa il vostro libro illustrato.',
    description: 'Racconta un desiderio, usa una foto come riferimento se vuoi e ascoltate insieme. Sicuro e adatto all’età.',
    familyMode: 'Modalità famiglia',
    previewHeading: 'Come nasce il vostro sogno',
    previewDescription: 'Un piccolo sguardo in Lumi prima della registrazione.',
    steps: ['Racconta un desiderio', 'Scegli personaggio e voce', 'Leggete insieme'],
    wish: 'Un’avventura nella foresta delle nuvole …',
    photo: 'La tua foto resta privata',
    book: 'Il vostro libro illustrato digitale',
    start: 'Apri il mondo delle storie',
    privacy: 'Le foto dei bambini non vengono mai mostrate pubblicamente né archiviate nella libreria.',
  },
  ar: {
    eyebrow: 'قصتك. عائلتك. سحركم.',
    heading: 'كل فكرة قبل النوم تصبح كتابكم المصوّر الخاص.',
    description: 'اخبرنا بالأمنية، واستخدم صورة كمرجع إن أحببت، ثم استمعوا معًا. تجربة آمنة ومناسبة للعمر للعائلات.',
    familyMode: 'وضع العائلة',
    previewHeading: 'كيف يولد حلمكم',
    previewDescription: 'نظرة صغيرة إلى Lumi قبل إنشاء الحساب.',
    steps: ['احكِ أمنية', 'اختر الشخصية والصوت', 'اقرؤوا معًا'],
    wish: 'مغامرة في غابة السحب …',
    photo: 'صورتك تبقى خاصة',
    book: 'كتابكم المصوّر الرقمي',
    start: 'افتح عالم الحكايات',
    privacy: 'لا تُعرض صور الأطفال علنًا ولا تُحفَظ في المكتبة.',
  },
  hi: {
    eyebrow: 'आपकी कहानी। आपका परिवार। आपका जादू।',
    heading: 'सोने से पहले की हर कल्पना आपकी अपनी चित्र-पुस्तक बनती है।',
    description: 'एक इच्छा बताइए, चाहें तो फोटो को संदर्भ बनाइए और साथ में सुनिए। सुरक्षित, उम्र के अनुसार और परिवारों के लिए।',
    familyMode: 'परिवार मोड',
    previewHeading: 'आपका सपना कैसे बनता है',
    previewDescription: 'साइन-अप से पहले Lumi की एक झलक।',
    steps: ['इच्छा बताइए', 'किरदार और आवाज़ चुनिए', 'साथ में पढ़िए'],
    wish: 'बादलों के जंगल में रोमांच …',
    photo: 'आपकी फोटो निजी रहती है',
    book: 'आपकी डिजिटल चित्र-पुस्तक',
    start: 'कहानी की दुनिया खोलें',
    privacy: 'बच्चों की तस्वीरें कभी सार्वजनिक नहीं दिखाई जातीं और न ही लाइब्रेरी में रखी जाती हैं।',
  },
};

type LandingExampleCopy = {
  kicker: string;
  heading: string;
  description: string;
  source: string;
  result: string;
  note: string;
  private: string;
};

// Public landing pages must explain the photo-to-illustration promise with a
// clearly labelled fictional example. No family upload is ever used here.
const LANDING_EXAMPLE_COPY: Record<LanguageCode, LandingExampleCopy> = {
  de: { kicker: 'So einfach funktioniert Lumi', heading: 'Euer Kind wird zum Helden der Geschichte.', description: 'Erzählt Lumi euren Wunsch. Auf Wunsch entsteht aus einem Foto eine liebevoll gezeichnete Figur für euer persönliches Abenteuer.', source: 'Euer Lieblingsfoto', result: 'Eure Bilderbuchfigur', note: '', private: 'Das Foto ist freiwillig. Ihr könnt auch ganz ohne Foto starten.' },
  en: { kicker: 'Lumi in three simple steps', heading: 'An idea becomes an illustrated main character.', description: 'First, tell Lumi the wish. If you choose, Lumi uses a photo as a reference and creates an illustrated character for your story.', source: 'Example photo', result: 'Lumi illustration', note: 'Fictional example - not a customer photo', private: 'A real child photo is processed only with your explicit consent.' },
  fr: { kicker: 'Lumi en trois étapes simples', heading: 'Une idée devient un héros illustré.', description: 'Commencez par raconter votre souhait. Si vous le voulez, Lumi utilise une photo comme référence et dessine une figure pour votre histoire.', source: 'Photo exemple', result: 'Illustration Lumi', note: 'Exemple fictif - pas une photo de client', private: 'Une vraie photo d’enfant n’est traitée qu’avec votre consentement explicite.' },
  es: { kicker: 'Lumi en tres sencillos pasos', heading: 'Una idea se convierte en un protagonista ilustrado.', description: 'Primero cuenta el deseo. Si quieres, Lumi usa una foto como referencia y dibuja un personaje para vuestra historia.', source: 'Foto de ejemplo', result: 'Ilustración Lumi', note: 'Ejemplo ficticio - no es una foto de cliente', private: 'Una foto real de un niño solo se procesa con tu consentimiento explícito.' },
  zh: { kicker: 'Lumi 只需三步', heading: '一个想法会变成绘本主角。', description: '先告诉 Lumi 你们的愿望。如你选择，Lumi 会把照片作为参考，为故事绘制角色。', source: '示例照片', result: 'Lumi 绘图', note: '虚构示例，并非客户照片', private: '真实儿童照片只会在你明确同意后才被处理。' },
  ja: { kicker: 'Lumi は簡単な3ステップ', heading: 'アイデアが絵本の主人公になります。', description: 'まず願いを伝えます。必要なら写真を参考に、Lumi が物語のためのイラストの主人公を描きます。', source: 'サンプル写真', result: 'Lumi のイラスト', note: '架空の例であり、お客様の写真ではありません', private: '実際のお子さまの写真は、明確な同意がある場合のみ処理されます。' },
  it: { kicker: 'Lumi in tre semplici passi', heading: 'Un’idea diventa un protagonista illustrato.', description: 'Prima racconta il desiderio. Se vuoi, Lumi usa una foto come riferimento e disegna un personaggio per la vostra storia.', source: 'Foto di esempio', result: 'Illustrazione Lumi', note: 'Esempio fittizio - non è una foto di cliente', private: 'Una vera foto di un bambino viene elaborata solo con il tuo consenso esplicito.' },
  ar: { kicker: 'Lumi في ثلاث خطوات بسيطة', heading: 'الفكرة تصبح بطلاً مرسوماً في القصة.', description: 'أخبر Lumi بالأمنية أولاً. وإذا اخترت ذلك، تستخدم Lumi الصورة كمرجع وترسم شخصية لقصتكم.', source: 'صورة توضيحية', result: 'رسم Lumi', note: 'مثال خيالي وليس صورة عميل', private: 'لا تتم معالجة صورة طفل حقيقية إلا بموافقتك الصريحة.' },
  hi: { kicker: 'Lumi के तीन आसान कदम', heading: 'एक कल्पना चित्रित मुख्य किरदार बनती है।', description: 'पहले अपनी इच्छा बताइए। यदि आप चाहें, Lumi फोटो को संदर्भ की तरह लेकर आपकी कहानी का चित्रित किरदार बनाता है।', source: 'उदाहरण फोटो', result: 'Lumi चित्र', note: 'काल्पनिक उदाहरण - ग्राहक की फोटो नहीं', private: 'किसी बच्चे की असली फोटो केवल आपकी स्पष्ट अनुमति के बाद ही प्रोसेस की जाती है।' },
};

const MARKETING_CONSENT_COPY: Record<LanguageCode, { registration: string; title: string; description: string }> = {
  de: { registration: 'Ich möchte gelegentlich Neuigkeiten, neue Funktionen und Familienideen per E-Mail erhalten. Diese Einwilligung ist freiwillig und jederzeit widerrufbar.', title: 'Lumi-Neuigkeiten', description: 'Erhalte gelegentlich Familienideen und Produktneuigkeiten per E-Mail. Du kannst die Einwilligung jederzeit hier ausschalten.' },
  en: { registration: 'I would like occasional emails about new features and family story ideas. This is optional and can be withdrawn at any time.', title: 'Lumi news', description: 'Receive occasional family ideas and product news by email. You can turn this off here at any time.' },
  fr: { registration: 'Je souhaite recevoir occasionnellement des nouveautés et des idées d’histoires familiales par e-mail. Ce choix est facultatif et révocable à tout moment.', title: 'Actualités Lumi', description: 'Recevez parfois des idées familiales et des nouveautés par e-mail. Vous pouvez désactiver ce choix ici à tout moment.' },
  es: { registration: 'Quiero recibir ocasionalmente novedades e ideas de cuentos familiares por correo. Es opcional y puedo retirar mi permiso en cualquier momento.', title: 'Novedades de Lumi', description: 'Recibe de vez en cuando ideas familiares y novedades por correo. Puedes desactivarlo aquí cuando quieras.' },
  zh: { registration: '我愿意偶尔通过电子邮件接收新功能和家庭故事灵感。此项为自愿选择，可随时取消。', title: 'Lumi 新消息', description: '偶尔通过电子邮件接收家庭灵感和产品新消息。你可以随时在此关闭。' },
  ja: { registration: '新機能や家族向けの物語アイデアを、ときどきメールで受け取ります。任意で、いつでも解除できます。', title: 'Lumi からのお知らせ', description: '家族向けのアイデアや新機能を、ときどきメールでお届けします。ここでいつでも解除できます。' },
  it: { registration: 'Desidero ricevere occasionalmente novità e idee per storie di famiglia via e-mail. È facoltativo e revocabile in qualsiasi momento.', title: 'Novità Lumi', description: 'Ricevi ogni tanto idee per famiglie e novità via e-mail. Puoi disattivare questa scelta qui in qualsiasi momento.' },
  ar: { registration: 'أرغب في تلقي أخبار وميزات جديدة وأفكار عائلية عبر البريد الإلكتروني من حين لآخر. هذا اختياري ويمكن إلغاؤه في أي وقت.', title: 'أخبار Lumi', description: 'استلم أفكارًا عائلية وأخبار الميزات عبر البريد الإلكتروني من حين لآخر. يمكنك إيقاف ذلك هنا في أي وقت.' },
  hi: { registration: 'मैं कभी-कभी नई सुविधाओं और परिवार के लिए कहानी के विचारों की ईमेल पाना चाहता/चाहती हूँ। यह वैकल्पिक है और कभी भी बंद किया जा सकता है।', title: 'Lumi की खबरें', description: 'कभी-कभी परिवार के लिए विचार और नई सुविधाओं की ईमेल पाएँ। इसे यहाँ कभी भी बंद किया जा सकता है।' },
};

// This is only a thoughtful default, never a hidden geo decision. Browser
// language and timezone are available without collecting an IP address or
// asking a family for location permission; they can always choose another
// language before creating the account.
function detectSuggestedLanguage(): LanguageCode {
  if (typeof navigator === 'undefined') return 'en';
  const locales = [...(navigator.languages || []), navigator.language].filter(Boolean);
  for (const locale of locales) {
    const normalized = String(locale).toLowerCase();
    const base = normalized.split('-')[0] as LanguageCode;
    if (base === 'hi' || /-in$/.test(normalized)) return 'hi';
    if (SUPPORTED_LANGUAGE_CODES.has(base)) return base;
  }
  try {
    if (Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Kolkata') return 'hi';
  } catch {
    // Keep the international default when a browser withholds timezone data.
  }
  return 'en';
}

// These are the account, photo and voice-flow messages that were added after
// the original translation set. Keeping them here makes a language switch
// atomic: the interface never falls back to German when another language is
// selected.
const FLOW_TRANSLATIONS: Record<LanguageCode, Record<string, string>> = {
  de: {
    familyAccess: 'Sicherer Familienzugang mit E-Mail-Bestätigung und Passwort-Wiederherstellung.', minPasswordLength: 'Mindestens 10 Zeichen.', adultConfirmation: 'Ich bin mindestens 18 Jahre alt und richte Lumi als erziehungsberechtigte Person ein.', pleaseWait: 'Einen Moment …', createAccount: 'Konto erstellen', resetPassword: 'Passwort zurücksetzen', newPassword: 'Neues Passwort', changePassword: 'Passwort ändern', sendEmail: 'E-Mail senden', emailVerified: 'Deine E-Mail-Adresse ist bestätigt.', invalidVerification: 'Der Bestätigungslink ist ungültig oder abgelaufen.', passwordResetSent: 'Wenn ein Konto existiert, ist die E-Mail zum Zurücksetzen unterwegs.', passwordChangeConfirmed: 'Dein Passwort wurde geändert. Die Bestätigung ist per E-Mail unterwegs.', emailAlreadyVerified: 'Deine E-Mail-Adresse ist bereits bestätigt.', verificationSent: 'Die Bestätigungs-E-Mail ist unterwegs.', photoTooLarge: 'Das Foto darf höchstens 8 MB groß sein.', photoCheckFailed: 'Das Foto konnte nicht geprüft werden.', randomIdea: 'Überrasch mich', randomIdeaHint: 'Würfle eine neue, altersgerechte Traumidee.', photoRemove: 'Foto entfernen', photoReady: 'Foto ist bereit. Bitte Einwilligung bestätigen.', photoConsent: 'Ich bin erziehungsberechtigt und erlaube die einmalige Verarbeitung dieses Fotos, damit Lumi die Kinderfigur als Comic für diese Geschichte illustrieren kann. Das Originalfoto wird nicht in der Bibliothek gespeichert.', photoConsentRequired: 'Bitte bestätige zuerst die Einwilligung für das Kinderfoto.', photoReference: 'Dein Foto dient nur als Vorlage für Gesicht, Haare und Aussehen der gezeichneten Kinderfigur.', photoDiscarded: 'Geschichte gewebt! Das Referenzfoto wurde aus Lumi entfernt.', testStories: 'Testgeschichten', emailVerificationTitle: 'E-Mail noch bestätigen', emailVerificationText: 'Öffne den Link in deiner Willkommens-E-Mail. Danach stehen euch drei Testgeschichten zur Verfügung.', resendEmail: 'E-Mail erneut senden', sending: 'Wird gesendet …', accountSecurity: 'Konto & Sicherheit', changePasswordText: 'Wähle ein neues Passwort mit mindestens 10 Zeichen. Du bekommst danach eine Bestätigung per E-Mail.', currentPassword: 'Aktuelles Passwort', securePasswordChange: 'Passwort sicher ändern', helpSupport: 'Hilfe & Support', supportTitle: 'Wie können wir helfen?', supportText: 'Schreib uns dein Anliegen. Du bekommst sofort eine Bestätigung per E-Mail, und das Lumi-Team meldet sich bei dir.', subject: 'Thema', supportSubjectHint: 'Zum Beispiel: Frage zu meinem Abo', message: 'Deine Nachricht', supportMessageHint: 'Beschreibe kurz, wobei wir helfen können.', sendMessage: 'Nachricht senden', manageSubscription: 'Abo verwalten', monthly: '9,99 € monatlich', yearly: '99,99 € jährlich · 2 Monate gespart', stripeInfo: 'Sichere Zahlung über Stripe. Das Abo verlängert sich automatisch und kann im Kundenportal gekündigt werden.', voiceHint: 'Antippen, sofort hören und auswählen. Die längere KI-Stimme wird im Hintergrund für die nächste Buchseite vorbereitet.', voicePreview: 'Hörprobe …', voiceAria: 'auswählen und Hörprobe abspielen', narrationPreparing: 'Erzählerstimme wird vorbereitet …', profile: 'Profil', titleImage: 'Titelbild', defaultAdventure: 'Ein magisches Abenteuer', randomIdeaReady: 'Eine neue Traumidee ist gelandet. ✨', deleteAllStories: 'Alle gespeicherten Geschichten und Bilder löschen', deleteAllStoriesTitle: 'Alle Bilderbücher löschen?', deleteAllStoriesText: 'Die Geschichten und alle dazugehörigen gespeicherten Illustrationen werden sofort und endgültig aus deiner Bibliothek gelöscht.', allStoriesDeleted: 'Alle gespeicherten Geschichten und Bilder wurden gelöscht.', deleteStoriesFailed: 'Die Bibliothek konnte nicht vollständig gelöscht werden.', printBook: 'Als Buch bestellen', printBookHint: 'Adresse und sichere Zahlung erfolgen erst bei Stripe.',
  },
  en: {
    familyAccess: 'A secure family space with email confirmation and password recovery.', minPasswordLength: 'At least 10 characters.', adultConfirmation: 'I am at least 18 years old and am setting up Lumi as a parent or legal guardian.', pleaseWait: 'Just a moment …', createAccount: 'Create account', resetPassword: 'Reset password', newPassword: 'New password', changePassword: 'Change password', sendEmail: 'Send email', emailVerified: 'Your email address is confirmed.', invalidVerification: 'The confirmation link is invalid or has expired.', passwordResetSent: 'If an account exists, a reset email is on its way.', passwordChangeConfirmed: 'Your password was changed. A confirmation email is on its way.', emailAlreadyVerified: 'Your email address is already confirmed.', verificationSent: 'The confirmation email is on its way.', photoTooLarge: 'The photo must be 8 MB or smaller.', photoCheckFailed: 'The photo could not be checked.', randomIdea: 'Surprise me', randomIdeaHint: 'Roll a new age-appropriate dream idea.', photoRemove: 'Remove photo', photoReady: 'Photo is ready. Please confirm your consent.', photoConsent: 'I am a parent or legal guardian and allow this photo to be processed once so Lumi can illustrate the child as a comic character for this story. The original photo is not stored in the library.', photoConsentRequired: 'Please confirm consent for the child photo first.', photoReference: 'Your photo is only a reference for the illustrated child character’s face, hair and appearance.', photoDiscarded: 'Story woven! The reference photo was removed from Lumi.', testStories: 'Trial stories', emailVerificationTitle: 'Confirm your email', emailVerificationText: 'Open the link in your welcome email. Then your three trial stories will be available.', resendEmail: 'Resend email', sending: 'Sending …', accountSecurity: 'Account & security', changePasswordText: 'Choose a new password with at least 10 characters. You will receive a confirmation email afterwards.', currentPassword: 'Current password', securePasswordChange: 'Change password securely', helpSupport: 'Help & support', supportTitle: 'How can we help?', supportText: 'Tell us what you need. You will receive an immediate email confirmation, and the Lumi team will get back to you.', subject: 'Subject', supportSubjectHint: 'For example: a question about my subscription', message: 'Your message', supportMessageHint: 'Briefly describe how we can help.', sendMessage: 'Send message', manageSubscription: 'Manage subscription', monthly: '€9.99 monthly', yearly: '€99.99 yearly · save 2 months', stripeInfo: 'Secure payment through Stripe. Your subscription renews automatically and can be cancelled in the customer portal.', voiceHint: 'Tap to hear it instantly and choose. The longer AI voice is prepared in the background for the next book page.', voicePreview: 'Preview …', voiceAria: 'select and play voice preview', narrationPreparing: 'Preparing narrator voice …', profile: 'Profile', titleImage: 'Cover image', defaultAdventure: 'A magical adventure', randomIdeaReady: 'A new dream idea has arrived. ✨', deleteAllStories: 'Delete all saved stories and images', deleteAllStoriesTitle: 'Delete all picture books?', deleteAllStoriesText: 'Stories and every stored illustration will be removed from your library immediately and permanently.', allStoriesDeleted: 'All saved stories and images were deleted.', deleteStoriesFailed: 'The library could not be deleted completely.', printBook: 'Order as a book', printBookHint: 'Address and secure payment are collected by Stripe only.',
  },
  fr: {
    familyAccess: 'Un espace familial sécurisé avec confirmation par e-mail et récupération du mot de passe.', minPasswordLength: 'Au moins 10 caractères.', adultConfirmation: 'J’ai au moins 18 ans et je configure Lumi en tant que parent ou tuteur légal.', pleaseWait: 'Un instant …', createAccount: 'Créer un compte', resetPassword: 'Réinitialiser le mot de passe', newPassword: 'Nouveau mot de passe', changePassword: 'Changer le mot de passe', sendEmail: 'Envoyer l’e-mail', randomIdea: 'Surprends-moi', randomIdeaHint: 'Lance une nouvelle idée de rêve adaptée à l’âge.', photoRemove: 'Supprimer la photo', photoReady: 'La photo est prête. Confirmez votre accord.', photoConsent: 'Je suis parent ou tuteur légal et j’autorise le traitement unique de cette photo afin que Lumi illustre l’enfant comme personnage de bande dessinée pour cette histoire. La photo originale n’est pas enregistrée.', photoConsentRequired: 'Veuillez d’abord confirmer l’accord pour la photo de l’enfant.', photoReference: 'Votre photo sert uniquement de modèle pour le visage, les cheveux et l’apparence du personnage illustré.', testStories: 'Histoires d’essai', emailVerificationTitle: 'Confirmez votre e-mail', emailVerificationText: 'Ouvrez le lien dans votre e-mail de bienvenue. Vos trois histoires d’essai seront alors disponibles.', resendEmail: 'Renvoyer l’e-mail', sending: 'Envoi …', accountSecurity: 'Compte et sécurité', changePasswordText: 'Choisissez un nouveau mot de passe d’au moins 10 caractères. Vous recevrez ensuite un e-mail de confirmation.', currentPassword: 'Mot de passe actuel', securePasswordChange: 'Changer le mot de passe', helpSupport: 'Aide et support', supportTitle: 'Comment pouvons-nous aider ?', supportText: 'Écrivez-nous votre demande. Vous recevrez une confirmation immédiate par e-mail.', subject: 'Sujet', supportSubjectHint: 'Par exemple : question sur mon abonnement', message: 'Votre message', supportMessageHint: 'Décrivez brièvement votre besoin.', sendMessage: 'Envoyer le message', manageSubscription: 'Gérer l’abonnement', monthly: '9,99 € par mois', yearly: '99,99 € par an · 2 mois offerts', stripeInfo: 'Paiement sécurisé via Stripe. L’abonnement se renouvelle automatiquement et peut être résilié dans le portail client.', voiceHint: 'Touchez pour écouter immédiatement et choisir. La voix IA longue est préparée en arrière-plan.', voicePreview: 'Extrait …', voiceAria: 'sélectionner et écouter un extrait', narrationPreparing: 'Préparation de la voix du narrateur …', profile: 'Profil', titleImage: 'Image de couverture', defaultAdventure: 'Une aventure magique', randomIdeaReady: 'Une nouvelle idée de rêve est arrivée. ✨',
  },
  es: {
    familyAccess: 'Un espacio familiar seguro con confirmación por correo y recuperación de contraseña.', minPasswordLength: 'Al menos 10 caracteres.', adultConfirmation: 'Tengo al menos 18 años y configuro Lumi como madre, padre o tutor legal.', pleaseWait: 'Un momento …', createAccount: 'Crear cuenta', resetPassword: 'Restablecer contraseña', newPassword: 'Nueva contraseña', changePassword: 'Cambiar contraseña', sendEmail: 'Enviar correo', randomIdea: 'Sorpréndeme', randomIdeaHint: 'Lanza una nueva idea de sueño adecuada para la edad.', photoRemove: 'Eliminar foto', photoReady: 'La foto está lista. Confirma tu consentimiento.', photoConsent: 'Soy madre, padre o tutor legal y permito el procesamiento único de esta foto para que Lumi ilustre al niño como personaje de cómic para esta historia. La foto original no se guarda en la biblioteca.', photoConsentRequired: 'Confirma primero el consentimiento para la foto del niño.', photoReference: 'Tu foto solo se usa como referencia para la cara, el cabello y el aspecto del personaje ilustrado.', testStories: 'Historias de prueba', emailVerificationTitle: 'Confirma tu correo', emailVerificationText: 'Abre el enlace de tu correo de bienvenida. Entonces estarán disponibles tus tres historias de prueba.', resendEmail: 'Reenviar correo', sending: 'Enviando …', accountSecurity: 'Cuenta y seguridad', changePasswordText: 'Elige una contraseña nueva de al menos 10 caracteres. Después recibirás un correo de confirmación.', currentPassword: 'Contraseña actual', securePasswordChange: 'Cambiar contraseña', helpSupport: 'Ayuda y soporte', supportTitle: '¿Cómo podemos ayudarte?', supportText: 'Cuéntanos lo que necesitas. Recibirás una confirmación inmediata por correo.', subject: 'Asunto', supportSubjectHint: 'Por ejemplo: una pregunta sobre mi suscripción', message: 'Tu mensaje', supportMessageHint: 'Describe brevemente cómo podemos ayudar.', sendMessage: 'Enviar mensaje', manageSubscription: 'Gestionar suscripción', monthly: '9,99 € al mes', yearly: '99,99 € al año · ahorra 2 meses', stripeInfo: 'Pago seguro con Stripe. La suscripción se renueva automáticamente y puede cancelarse en el portal del cliente.', voiceHint: 'Toca para escuchar al instante y elegir. La voz larga de IA se prepara en segundo plano.', voicePreview: 'Vista previa …', voiceAria: 'seleccionar y reproducir vista previa', narrationPreparing: 'Preparando voz narradora …', profile: 'Perfil', titleImage: 'Imagen de portada', defaultAdventure: 'Una aventura mágica', randomIdeaReady: 'Ha llegado una nueva idea de sueño. ✨',
  },
  zh: {
    familyAccess: '安全的家庭空间，提供邮箱验证和密码找回。', minPasswordLength: '至少 10 个字符。', adultConfirmation: '我已年满 18 岁，并以父母或法定监护人的身份设置 Lumi。', pleaseWait: '请稍候 …', createAccount: '创建账户', resetPassword: '重设密码', newPassword: '新密码', changePassword: '更改密码', sendEmail: '发送邮件', randomIdea: '给我惊喜', randomIdeaHint: '掷出一个新的、适合年龄的梦想点子。', photoRemove: '移除照片', photoReady: '照片已准备好。请确认同意。', photoConsent: '我是父母或法定监护人，同意仅处理一次这张照片，以便 Lumi 将孩子绘制成适合本故事的漫画角色。原始照片不会保存在图书馆中。', photoConsentRequired: '请先确认儿童照片的授权。', photoReference: '你的照片仅用作绘制角色的脸部、发型和外观参考。', testStories: '试用故事', emailVerificationTitle: '确认你的邮箱', emailVerificationText: '打开欢迎邮件中的链接，即可使用三篇试用故事。', resendEmail: '重新发送邮件', sending: '正在发送 …', accountSecurity: '账户与安全', changePasswordText: '请选择至少 10 个字符的新密码。之后你会收到确认邮件。', currentPassword: '当前密码', securePasswordChange: '安全更改密码', helpSupport: '帮助与支持', supportTitle: '我们如何帮助你？', supportText: '告诉我们你的需求。你会立即收到邮件确认。', subject: '主题', supportSubjectHint: '例如：关于订阅的问题', message: '你的消息', supportMessageHint: '请简要说明我们如何帮助你。', sendMessage: '发送消息', manageSubscription: '管理订阅', monthly: '每月 9.99 欧元', yearly: '每年 99.99 欧元 · 节省 2 个月', stripeInfo: '通过 Stripe 安全付款。订阅会自动续订，可在客户门户中取消。', voiceHint: '轻点即可试听并选择。较长的 AI 语音会在后台为下一页准备。', voicePreview: '试听 …', voiceAria: '选择并播放语音试听', narrationPreparing: '正在准备旁白声音 …', profile: '个人资料', titleImage: '封面图片', defaultAdventure: '一场神奇的冒险', randomIdeaReady: '新的梦想点子来了。✨',
  },
  ja: {
    familyAccess: 'メール確認とパスワード再設定ができる、安全な家族のための空間です。', minPasswordLength: '10 文字以上。', adultConfirmation: '私は 18 歳以上で、保護者として Lumi を設定しています。', pleaseWait: '少々お待ちください …', createAccount: 'アカウントを作成', resetPassword: 'パスワードをリセット', newPassword: '新しいパスワード', changePassword: 'パスワードを変更', sendEmail: 'メールを送信', randomIdea: 'おまかせ', randomIdeaHint: '年齢に合った新しい夢のアイデアを出します。', photoRemove: '写真を削除', photoReady: '写真の準備ができました。許可を確認してください。', photoConsent: '私は親権者または法定後見人として、この写真を一度だけ処理し、Lumi がこの物語の子どもを漫画風キャラクターとして描くことを許可します。元の写真は本棚に保存されません。', photoConsentRequired: '最初に子どもの写真への同意を確認してください。', photoReference: '写真は、描かれる子どもの顔・髪型・見た目の参考にのみ使われます。', testStories: 'お試しストーリー', emailVerificationTitle: 'メールを確認', emailVerificationText: 'ウェルカムメールのリンクを開くと、3 つのお試しストーリーが利用できます。', resendEmail: 'メールを再送', sending: '送信中 …', accountSecurity: 'アカウントとセキュリティ', changePasswordText: '10 文字以上の新しいパスワードを選んでください。確認メールが届きます。', currentPassword: '現在のパスワード', securePasswordChange: '安全にパスワードを変更', helpSupport: 'ヘルプとサポート', supportTitle: 'どのようにお手伝いできますか？', supportText: 'お問い合わせ内容をお知らせください。すぐに確認メールをお送りします。', subject: '件名', supportSubjectHint: '例：サブスクリプションについての質問', message: 'メッセージ', supportMessageHint: 'お困りの内容を簡単に説明してください。', sendMessage: 'メッセージを送信', manageSubscription: 'サブスクリプションを管理', monthly: '月額 9.99 €', yearly: '年額 99.99 € · 2 か月お得', stripeInfo: 'Stripe による安全な決済です。サブスクリプションは自動更新され、カスターポータルで解約できます。', voiceHint: 'タップしてすぐに試聴し、選択できます。長い AI 音声は次のページのためにバックグラウンドで準備されます。', voicePreview: '試聴 …', voiceAria: '選択して試聴を再生', narrationPreparing: 'ナレーターの声を準備中 …', profile: 'プロフィール', titleImage: '表紙画像', defaultAdventure: '魔法の冒険', randomIdeaReady: '新しい夢のアイデアが届きました。✨',
  },
  it: {
    familyAccess: 'Uno spazio familiare sicuro con conferma e-mail e recupero della password.', minPasswordLength: 'Almeno 10 caratteri.', adultConfirmation: 'Ho almeno 18 anni e configuro Lumi come genitore o tutore legale.', pleaseWait: 'Un momento …', createAccount: 'Crea account', resetPassword: 'Reimposta password', newPassword: 'Nuova password', changePassword: 'Cambia password', sendEmail: 'Invia e-mail', randomIdea: 'Sorprendimi', randomIdeaHint: 'Estrai una nuova idea di sogno adatta all’età.', photoRemove: 'Rimuovi foto', photoReady: 'La foto è pronta. Conferma il consenso.', photoConsent: 'Sono un genitore o tutore legale e autorizzo l’elaborazione una tantum di questa foto affinché Lumi illustri il bambino come personaggio a fumetti per questa storia. La foto originale non viene salvata nella libreria.', photoConsentRequired: 'Conferma prima il consenso per la foto del bambino.', photoReference: 'La foto viene usata solo come riferimento per volto, capelli e aspetto del personaggio illustrato.', testStories: 'Storie di prova', emailVerificationTitle: 'Conferma la tua e-mail', emailVerificationText: 'Apri il link nella tua e-mail di benvenuto. Poi saranno disponibili tre storie di prova.', resendEmail: 'Invia di nuovo l’e-mail', sending: 'Invio …', accountSecurity: 'Account e sicurezza', changePasswordText: 'Scegli una nuova password di almeno 10 caratteri. Riceverai poi un’e-mail di conferma.', currentPassword: 'Password attuale', securePasswordChange: 'Cambia password in sicurezza', helpSupport: 'Aiuto e supporto', supportTitle: 'Come possiamo aiutarti?', supportText: 'Scrivici la tua richiesta. Riceverai subito una conferma via e-mail.', subject: 'Oggetto', supportSubjectHint: 'Ad esempio: una domanda sul mio abbonamento', message: 'Il tuo messaggio', supportMessageHint: 'Descrivi brevemente come possiamo aiutarti.', sendMessage: 'Invia messaggio', manageSubscription: 'Gestisci abbonamento', monthly: '9,99 € al mese', yearly: '99,99 € all’anno · risparmi 2 mesi', stripeInfo: 'Pagamento sicuro tramite Stripe. L’abbonamento si rinnova automaticamente e può essere annullato nel portale clienti.', voiceHint: 'Tocca per ascoltare subito e scegliere. La voce IA più lunga viene preparata in sottofondo.', voicePreview: 'Anteprima …', voiceAria: 'seleziona e riproduci anteprima vocale', narrationPreparing: 'Preparazione della voce narrante …', profile: 'Profilo', titleImage: 'Immagine di copertina', defaultAdventure: 'Un’avventura magica', randomIdeaReady: 'È arrivata una nuova idea di sogno. ✨',
  },
  ar: {
    familyAccess: 'مساحة عائلية آمنة مع تأكيد البريد الإلكتروني واستعادة كلمة المرور.', minPasswordLength: '10 أحرف على الأقل.', adultConfirmation: 'أبلغ من العمر 18 عامًا على الأقل وأُعد Lumi بصفتي والدًا أو وصيًا قانونيًا.', pleaseWait: 'لحظة من فضلك …', createAccount: 'إنشاء حساب', resetPassword: 'إعادة تعيين كلمة المرور', newPassword: 'كلمة المرور الجديدة', changePassword: 'تغيير كلمة المرور', sendEmail: 'إرسال البريد', randomIdea: 'فاجئني', randomIdeaHint: 'احصل على فكرة حلم جديدة مناسبة للعمر.', photoRemove: 'إزالة الصورة', photoReady: 'الصورة جاهزة. يرجى تأكيد الموافقة.', photoConsent: 'أنا والد أو وصي قانوني وأسمح بمعالجة هذه الصورة مرة واحدة لكي يرسم Lumi الطفل كشخصية كرتونية لهذه القصة. لا تُحفظ الصورة الأصلية في المكتبة.', photoConsentRequired: 'يرجى تأكيد الموافقة على صورة الطفل أولًا.', photoReference: 'تُستخدم صورتك كمرجع فقط لوجه الشخصية المرسومة وشعرها ومظهرها.', testStories: 'قصص تجريبية', emailVerificationTitle: 'أكّد بريدك الإلكتروني', emailVerificationText: 'افتح الرابط في رسالة الترحيب، ثم ستتوفر لك ثلاث قصص تجريبية.', resendEmail: 'إعادة إرسال البريد', sending: 'جارٍ الإرسال …', accountSecurity: 'الحساب والأمان', changePasswordText: 'اختر كلمة مرور جديدة لا تقل عن 10 أحرف. ستصلك رسالة تأكيد بعد ذلك.', currentPassword: 'كلمة المرور الحالية', securePasswordChange: 'تغيير كلمة المرور بأمان', helpSupport: 'المساعدة والدعم', supportTitle: 'كيف يمكننا مساعدتك؟', supportText: 'أرسل لنا طلبك. ستتلقى تأكيدًا فوريًا عبر البريد الإلكتروني.', subject: 'الموضوع', supportSubjectHint: 'مثال: سؤال حول اشتراكي', message: 'رسالتك', supportMessageHint: 'اشرح باختصار كيف يمكننا المساعدة.', sendMessage: 'إرسال الرسالة', manageSubscription: 'إدارة الاشتراك', monthly: '9.99 € شهريًا', yearly: '99.99 € سنويًا · توفير شهرين', stripeInfo: 'دفع آمن عبر Stripe. يتجدد الاشتراك تلقائيًا ويمكن إلغاؤه من بوابة العملاء.', voiceHint: 'المس للاستماع فورًا والاختيار. يجري تجهيز الصوت الأطول بالذكاء الاصطناعي للصفحة التالية في الخلفية.', voicePreview: 'استماع …', voiceAria: 'اختيار وتشغيل معاينة الصوت', narrationPreparing: 'يجري تجهيز صوت الراوي …', profile: 'الملف الشخصي', titleImage: 'صورة الغلاف', defaultAdventure: 'مغامرة سحرية', randomIdeaReady: 'وصلت فكرة حلم جديدة. ✨',
  },
  hi: {
    familyAccess: 'ईमेल पुष्टि और पासवर्ड रिकवरी के साथ सुरक्षित पारिवारिक स्थान।', minPasswordLength: 'कम-से-कम 10 अक्षर।', adultConfirmation: 'मेरी आयु कम-से-कम 18 वर्ष है और मैं Lumi को माता-पिता या कानूनी अभिभावक के रूप में सेट कर रहा/रही हूँ।', pleaseWait: 'एक क्षण …', createAccount: 'खाता बनाएँ', resetPassword: 'पासवर्ड रीसेट करें', newPassword: 'नया पासवर्ड', changePassword: 'पासवर्ड बदलें', sendEmail: 'ईमेल भेजें', emailVerified: 'आपका ईमेल पता सत्यापित हो गया है।', invalidVerification: 'पुष्टि लिंक अमान्य है या समय समाप्त हो गया है।', passwordResetSent: 'यदि खाता मौजूद है, तो रीसेट ईमेल भेज दिया गया है।', passwordChangeConfirmed: 'आपका पासवर्ड बदल दिया गया है। पुष्टि ईमेल भेज दिया गया है।', emailAlreadyVerified: 'आपका ईमेल पता पहले से सत्यापित है।', verificationSent: 'पुष्टि ईमेल भेज दिया गया है।', photoTooLarge: 'फोटो अधिकतम 8 MB का हो सकता है।', photoCheckFailed: 'फोटो जाँचा नहीं जा सका।', randomIdea: 'मुझे चौंकाएँ', randomIdeaHint: 'उम्र के अनुकूल नई सपनों की कहानी चुनें।', photoRemove: 'फोटो हटाएँ', photoReady: 'फोटो तैयार है। कृपया अनुमति दें।', photoConsent: 'मैं माता-पिता या कानूनी अभिभावक हूँ और इस फोटो को केवल एक बार संसाधित करने की अनुमति देता/देती हूँ, ताकि Lumi इस कहानी के लिए बच्चे को कार्टून पात्र के रूप में चित्रित कर सके। मूल फोटो लाइब्रेरी में नहीं रखा जाता।', photoConsentRequired: 'पहले बच्चे के फोटो के लिए अनुमति की पुष्टि करें।', photoReference: 'आपका फोटो केवल चित्रित बच्चे के चेहरे, बालों और रूप-रंग के संदर्भ के लिए है।', photoDiscarded: 'कहानी तैयार! संदर्भ फोटो Lumi से हटा दिया गया है।', testStories: 'परीक्षण कहानियाँ', emailVerificationTitle: 'अपना ईमेल सत्यापित करें', emailVerificationText: 'अपने स्वागत ईमेल में लिंक खोलें। उसके बाद तीन परीक्षण कहानियाँ उपलब्ध होंगी।', resendEmail: 'ईमेल फिर भेजें', sending: 'भेजा जा रहा है …', accountSecurity: 'खाता और सुरक्षा', changePasswordText: 'कम-से-कम 10 अक्षरों वाला नया पासवर्ड चुनें। इसके बाद आपको पुष्टि ईमेल मिलेगा।', currentPassword: 'वर्तमान पासवर्ड', securePasswordChange: 'सुरक्षित रूप से पासवर्ड बदलें', helpSupport: 'मदद और सहायता', supportTitle: 'हम आपकी कैसे मदद कर सकते हैं?', supportText: 'अपना संदेश लिखें। आपको तुरंत ईमेल पुष्टि मिलेगी और Lumi टीम संपर्क करेगी।', subject: 'विषय', supportSubjectHint: 'उदाहरण: मेरी सदस्यता के बारे में प्रश्न', message: 'आपका संदेश', supportMessageHint: 'संक्षेप में बताइए कि हम कैसे मदद कर सकते हैं।', sendMessage: 'संदेश भेजें', manageSubscription: 'सदस्यता प्रबंधित करें', monthly: '€9.99 प्रति माह', yearly: '€99.99 प्रति वर्ष · 2 महीने की बचत', stripeInfo: 'Stripe के माध्यम से सुरक्षित भुगतान। सदस्यता अपने-आप नवीनीकृत होती है और ग्राहक पोर्टल में रद्द की जा सकती है।', voiceHint: 'तुरंत सुनने और चुनने के लिए टैप करें। अगली पेज के लिए AI आवाज़ पीछे से तैयार होती है।', voicePreview: 'नमूना सुनें …', voiceAria: 'आवाज़ चुनें और नमूना चलाएँ', narrationPreparing: 'कथावाचक की आवाज़ तैयार हो रही है …', profile: 'प्रोफ़ाइल', titleImage: 'कवर चित्र', defaultAdventure: 'एक जादुई साहसिक यात्रा', randomIdeaReady: 'एक नया सपनों का विचार आ गया है। ✨', deleteAllStories: 'सभी सहेजी गई कहानियाँ और चित्र हटाएँ', deleteAllStoriesTitle: 'सारी चित्र-पुस्तकें हटाएँ?', deleteAllStoriesText: 'कहानियाँ और हर सहेजा गया चित्र आपकी लाइब्रेरी से तुरंत और हमेशा के लिए हट जाएगा।', allStoriesDeleted: 'सभी सहेजी गई कहानियाँ और चित्र हटा दिए गए।', deleteStoriesFailed: 'लाइब्रेरी पूरी तरह हटाई नहीं जा सकी।',
  },
};

const PRIVATE_PHOTO_COPY: Record<LanguageCode, { remember: string; description: string; saved: string; preparing: string }> = {
  de: { remember: 'Dieses Referenzfoto auf diesem Gerät merken', description: 'Nur auf diesem Gerät für dieses Familienkonto gespeichert. Du kannst es jederzeit mit dem Papierkorb löschen.', saved: 'Das Referenzfoto bleibt privat auf diesem Gerät gespeichert.', preparing: 'Foto wird vorbereitet …' },
  en: { remember: 'Remember this reference photo on this device', description: 'Stored only on this device for this family account. You can delete it with the bin at any time.', saved: 'The reference photo is kept privately on this device.', preparing: 'Preparing photo …' },
  fr: { remember: 'Mémoriser cette photo de référence sur cet appareil', description: 'Enregistrée uniquement sur cet appareil pour ce compte familial. Vous pouvez la supprimer à tout moment avec la corbeille.', saved: 'La photo de référence reste privée sur cet appareil.', preparing: 'Préparation de la photo …' },
  es: { remember: 'Recordar esta foto de referencia en este dispositivo', description: 'Se guarda solo en este dispositivo para esta cuenta familiar. Puedes eliminarla cuando quieras con la papelera.', saved: 'La foto de referencia queda guardada de forma privada en este dispositivo.', preparing: 'Preparando foto …' },
  zh: { remember: '在此设备上记住这张参考照片', description: '仅保存在此设备上的家庭账户中。可随时用删除按钮移除。', saved: '参考照片仅私密保存在此设备上。', preparing: '正在准备照片 …' },
  ja: { remember: 'この端末で参照写真を記憶する', description: 'この家族アカウントのこの端末にのみ保存されます。いつでも削除できます。', saved: '参照写真はこの端末だけに非公開で保存されます。', preparing: '写真を準備しています …' },
  it: { remember: 'Ricorda questa foto di riferimento su questo dispositivo', description: 'Salvata solo su questo dispositivo per questo account familiare. Puoi eliminarla in qualsiasi momento con il cestino.', saved: 'La foto di riferimento resta salvata privatamente su questo dispositivo.', preparing: 'Preparazione della foto …' },
  ar: { remember: 'تذكّر صورة المرجع هذه على هذا الجهاز', description: 'تُحفظ على هذا الجهاز فقط لهذا الحساب العائلي. يمكنك حذفها في أي وقت بزر سلة المهملات.', saved: 'تم حفظ صورة المرجع بشكل خاص على هذا الجهاز.', preparing: 'جارٍ تجهيز الصورة …' },
  hi: { remember: 'इस डिवाइस पर इस संदर्भ फोटो को याद रखें', description: 'केवल इस पारिवारिक खाते के लिए इसी डिवाइस पर रखा जाता है। इसे कभी भी कूड़ेदान से हटा सकते हैं।', saved: 'संदर्भ फोटो केवल इस डिवाइस पर निजी रूप से रखा गया है।', preparing: 'फोटो तैयार किया जा रहा है …' },
};

const HOW_IT_WORKS_COPY: Partial<Record<LanguageCode, { title: string; intro: string; story: string; photo: string; voice: string; saved: string }>> = {
  de: {
    title: 'So funktioniert dein Traum',
    intro: 'In vier kleinen Schritten entsteht euer persönliches Bilderbuch.',
    story: 'Schreib eine Idee auf – oder würfle eine Überraschung.',
    photo: 'Optional: Tippe auf Foto, wähle ein Bild und bestätige die Einwilligung. Lumi zeichnet daraus die Kinderfigur.',
    voice: 'Wähle eine Erzählerstimme. Sie liest die Geschichte später direkt vor.',
    saved: 'Fertig? Dein Bilderbuch landet automatisch in „Dein Nest“ und kann dort als Buch bestellt werden.',
  },
  en: {
    title: 'How your dream works',
    intro: 'Your personal picture book is created in four small steps.',
    story: 'Write an idea – or roll a surprise.',
    photo: 'Optional: tap Photo, choose an image and confirm consent. Lumi draws the child character from it.',
    voice: 'Choose a narrator voice. It will read the story aloud later.',
    saved: 'Done? Your picture book is saved automatically in “Your Nest” and can be ordered as a book there.',
  },
  ar: {
    title: 'كيف يعمل حلمك',
    intro: 'يتكوّن كتاب الصور الشخصي في أربع خطوات بسيطة.',
    story: 'اكتب فكرة أو اختر مفاجأة.',
    photo: 'اختياري: اضغط على الصورة، واختر صورة ووافق على المعالجة. يرسم Lumi الطفل منها كشخصية كرتونية.',
    voice: 'اختر صوت الراوي. سيقرأ القصة لاحقًا.',
    saved: 'انتهى؟ يُحفظ كتابك تلقائيًا في «عشك» ويمكن طلبه ككتاب من هناك.',
  },
};

const IconCheck = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={className} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
);

const IconCreditCard = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
);

const IconAlert = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
);

const IconShield = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
);

// --- MAIN APP COMPONENT ---
const LumiApp: React.FC = () => {
  const {
    user, loading: authLoading, login, signup, logout,
    resetPassword, completePasswordReset, changePassword, verifyEmail, deleteAccount,
    updateProfile: updateAuthProfile, refreshUser, resendEmailVerification,
  } = useAuth();
  const { usesRemaining, isPremium, upgrade, openBillingPortal, decrementUsage } = useSubscription();

  const [appState, setAppState] = useState<AppState>('SPLASH');
  const [profile, setProfile] = useState<ChildProfile>(() => ({ name: '', age: 4, voice: 'Kind', language: detectSuggestedLanguage() }));
  const [localPrompt, setLocalPrompt] = useState('');
  const [currentStory, setCurrentStory] = useState<Story | null>(null);
  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isContinuationLoading, setIsContinuationLoading] = useState(false);
  const [weavingStep, setWeavingStep] = useState<'STORY' | 'PAINTING' | 'VOICE'>('STORY');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isNarrationPreparing, setIsNarrationPreparing] = useState(false);
  const [previewVoice, setPreviewVoice] = useState<VoiceType | null>(null);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showLyrics, setShowLyrics] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [readerFocusMode, setReaderFocusMode] = useState(false);
  const [showReaderFocusHint, setShowReaderFocusHint] = useState(false);
  const [savedStories, setSavedStories] = useState<Story[]>([]);
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [safetyAlarm, setSafetyAlarm] = useState(false);
  const [authName, setAuthName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [adultConfirmed, setAdultConfirmed] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [passwordResetToken, setPasswordResetToken] = useState<string | null>(null);
  const [photoConsent, setPhotoConsent] = useState(false);
  const [rememberPhoto, setRememberPhoto] = useState(false);
  const [isRememberingPhoto, setIsRememberingPhoto] = useState(false);
  const [isPhotoPreparing, setIsPhotoPreparing] = useState(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [isRollingIdea, setIsRollingIdea] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordChangeSubmitting, setPasswordChangeSubmitting] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportError, setSupportError] = useState('');

  // Security & Confirmation States
  const [confirmModal, setConfirmModal] = useState<'LOGOUT' | 'DELETE' | 'DELETE_STORIES' | null>(null);
  const [isPrivacyShieldActive, setIsPrivacyShieldActive] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const playSessionIdRef = useRef<number>(0);
  const autoplayTimeoutRef = useRef<any>(null);
  const narrationFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const queryHandledRef = useRef(false);
  // Once the server portal has been read for an account, the bookshelf is
  // already current. This avoids a second request immediately after login.
  const portalLoadedForUserRef = useRef<string | null>(null);

  const t = useCallback((key: string): string => {
    const lang = profile.language || 'de';
    const val = FLOW_TRANSLATIONS[lang]?.[key] || TRANSLATIONS[lang]?.[key] || FLOW_TRANSLATIONS.en[key] || TRANSLATIONS.en?.[key] || key;
    return Array.isArray(val) ? val[0] : val;
  }, [profile.language]);
  const privatePhotoCopy = PRIVATE_PHOTO_COPY[profile.language] || PRIVATE_PHOTO_COPY.en;
  const howItWorksCopy = HOW_IT_WORKS_COPY[profile.language] || HOW_IT_WORKS_COPY.en!;
  const mobileReaderCopy = MOBILE_READER_COPY[profile.language] || MOBILE_READER_COPY.en;
  const readerFocusCopy = READER_FOCUS_COPY[profile.language] || READER_FOCUS_COPY.en;

  const waitForFinishedBook = useCallback(async (initialStory: Story, voice: VoiceType, language: LanguageCode) => {
    let completedStory = initialStory;

    // The server paints pages asynchronously so it can safely save progress
    // as it goes. The reading room, however, opens only once every page has a
    // finished illustration (or a clear final fallback) — never on a blank
    // placeholder mid-bedtime.
    while (completedStory.pages.some((page) => page.imagePending)) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 850));
      completedStory = await DBService.getStory(initialStory.id);
    }

    // Do not consume a complete story's narration quota before the family has
    // even opened page one. The reader requests the current page on demand
    // and softly prepares only the immediate next page during playback.
    // This keeps the voices responsive and avoids rate-limit bursts.

    return completedStory;
  }, []);

  useEffect(() => {
    if (appState !== 'STORY_BOOK' || isGenerating || isContinuationLoading || readerFocusMode || typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;

    const hintKey = 'lumi-reader-full-picture-tip-v1';
    if (window.localStorage.getItem(hintKey)) return;
    window.localStorage.setItem(hintKey, 'shown');
    setShowReaderFocusHint(true);
    const timeout = window.setTimeout(() => setShowReaderFocusHint(false), 7600);
    return () => window.clearTimeout(timeout);
  }, [appState, isGenerating, isContinuationLoading, readerFocusMode]);

  useEffect(() => {
    const language = profile.language || 'de';
    document.documentElement.lang = LANGUAGE_LOCALES[language];
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [profile.language]);

  const saveProfilePreferences = useCallback((nextProfile: ChildProfile) => {
    if (!user) return;
    // Child preferences are portable, but reference photos are not. They are
    // kept separately in private IndexedDB on this device only.
    const { photoUri: _photoUri, ...preferences } = nextProfile;
    localStorage.setItem(`profile_${user.uid}`, JSON.stringify(preferences));
    if (user.preferences?.language !== nextProfile.language || user.preferences?.voice !== nextProfile.voice) {
      void updateAuthProfile({ preferences: { language: nextProfile.language, voice: nextProfile.voice } }).catch((error) => {
        console.warn('Sprache oder Stimme konnte nicht im Konto gespeichert werden:', error);
      });
    }
  }, [user, updateAuthProfile]);

  const loadPrivatePhotoReference = useCallback(async (accountId: string) => {
    try {
      const photoUri = await getSavedPhotoReference(accountId);
      setRememberPhoto(Boolean(photoUri));
      setPhotoConsent(false);
      if (photoUri) setProfile((current) => ({ ...current, photoUri }));
    } catch (error) {
      // A family can still create a story if a browser blocks IndexedDB.
      console.warn('Privates Referenzfoto konnte nicht geladen werden:', error);
      setRememberPhoto(false);
    }
  }, []);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const localizedError = (error: any, fallbackKey = 'error') => {
    // Server-side validation is currently German. Do not leak a German error
    // into another selected interface language; use that language's friendly
    // generic message instead.
    return profile.language === 'de' && error?.message ? error.message : t(fallbackKey);
  };

  // --- PRIVACY SHIELD & SECURITY ---
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPrivacyShieldActive(document.hidden);
    };
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('contextmenu', handleContextMenu);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  useEffect(() => {
    if (queryHandledRef.current) return;
    queryHandledRef.current = true;
    const query = new URLSearchParams(window.location.search);
    const verificationToken = query.get('verifyEmail');
    const resetToken = query.get('resetPassword');

    if (resetToken) {
      setPasswordResetToken(resetToken);
      setAppState('FORGOT_PASSWORD');
    } else if (verificationToken) {
      verifyEmail(verificationToken)
        .then(() => showToast(t('emailVerified')))
        .catch((error) => showToast(localizedError(error, 'invalidVerification'), 'error'));
    }

    if (resetToken || verificationToken) {
      ['verifyEmail', 'resetPassword'].forEach((key) => query.delete(key));
      const cleaned = `${window.location.pathname}${query.size ? `?${query.toString()}` : ''}`;
      window.history.replaceState({}, '', cleaned);
    }
  }, [verifyEmail, showToast]);

  // Navigation Logic
  useEffect(() => {
    if (appState === 'SPLASH') {
      const timer = setTimeout(() => {
        if (!authLoading) {
          if (!user) setAppState('AUTH_GATE');
          else checkProfileAndNavigate(user);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [authLoading, appState, user]);

  useEffect(() => {
    if (!authLoading && user && (appState === 'AUTH_GATE' || appState === 'LOGIN' || appState === 'REGISTER')) {
      checkProfileAndNavigate(user);
    }
  }, [user, authLoading, appState]);

  const checkProfileAndNavigate = async (currentUser: UserAccount) => {
    // Local storage is only an offline safety net. The account portal is the
    // single source of truth so a name changed on a phone is the same on Mac.
    let localFallback: ChildProfile | null = null;
    const storedProfile = localStorage.getItem(`profile_${currentUser.uid}`);
    if (storedProfile) {
      try {
        const parsed = JSON.parse(storedProfile);
        if (parsed?.photoUri) delete parsed.photoUri;
        if (typeof parsed?.name === 'string' && parsed.name.trim().length >= 2) {
          localFallback = {
            ...parsed,
            name: parsed.name.trim(),
            age: Number(parsed.age) || 6,
            language: currentUser.preferences?.language || parsed.language || detectSuggestedLanguage(),
            voice: currentUser.preferences?.voice || parsed.voice || 'Kind',
          };
        }
      } catch {
        localStorage.removeItem(`profile_${currentUser.uid}`);
      }
    }

    try {
      const portal = await DBService.getPortal();
      portalLoadedForUserRef.current = currentUser.uid;
      setSavedStories(portal.stories);

      if (portal.profile) {
        const portableProfile: ChildProfile = {
          ...portal.profile,
          language: currentUser.preferences?.language || portal.profile.language || detectSuggestedLanguage(),
          voice: currentUser.preferences?.voice || portal.profile.voice || 'Kind',
        };
        setProfile(portableProfile);
        localStorage.setItem(`profile_${currentUser.uid}`, JSON.stringify(portableProfile));
        void loadPrivatePhotoReference(currentUser.uid);
        setAppState('HOME');
        return;
      }

      // Accounts from the first Lumi version may only have a local profile.
      // Migrate it once, then every later login uses the server profile.
      if (localFallback) {
        setProfile(localFallback);
        localStorage.setItem(`profile_${currentUser.uid}`, JSON.stringify(localFallback));
        void DBService.saveChildProfile(localFallback).catch((error) => console.warn('Lokales Profil konnte noch nicht übertragen werden:', error));
        void loadPrivatePhotoReference(currentUser.uid);
        setAppState('HOME');
        return;
      }
    } catch (error) {
      console.warn('Profil konnte nicht vom Lumi-Konto geladen werden:', error);
      // An already known family can still enter Lumi during a temporary outage.
      if (localFallback) {
        setProfile(localFallback);
        void loadPrivatePhotoReference(currentUser.uid);
        setAppState('HOME');
        return;
      }
    }

    setAppState('ONBOARDING');
  };

  const clearAuthFeedback = () => {
    setAuthError('');
    setAuthMessage('');
  };

  const handleAuthSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    clearAuthFeedback();
    setAuthSubmitting(true);
    try {
      if (appState === 'REGISTER') {
        await signup(authEmail.trim(), authPassword, authName.trim(), adultConfirmed, profile.language, marketingConsent);
      } else {
        await login(authEmail.trim(), authPassword);
      }
    } catch (error: any) {
      setAuthError(error.message || 'Anmeldung fehlgeschlagen.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handlePasswordAction = async (event: React.FormEvent) => {
    event.preventDefault();
    clearAuthFeedback();
    setAuthSubmitting(true);
    try {
      if (passwordResetToken) {
        await completePasswordReset(passwordResetToken, authPassword);
        setPasswordResetToken(null);
        setAuthMessage(t('passwordChangeConfirmed'));
      } else {
        await resetPassword(authEmail.trim());
        setAuthMessage(t('passwordResetSent'));
      }
    } catch (error: any) {
      setAuthError(localizedError(error));
    } finally {
      setAuthSubmitting(false);
    }
  };

  const loadLibrary = useCallback(async () => {
    if (user) {
      try {
        const stories = await DBService.getAllStories(user.uid);
        setSavedStories(stories);
      } catch (e) {
        console.error("Failed to load library from DB", e);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      portalLoadedForUserRef.current = null;
      setSavedStories([]);
      return;
    }
    if (appState !== 'SPLASH' && portalLoadedForUserRef.current !== user.uid) {
      void loadLibrary().then(() => { portalLoadedForUserRef.current = user.uid; });
    }
  }, [user, appState, loadLibrary]);

  const saveToLibrary = useCallback((story: Story) => {
    if (!user) return;
    // /api/stories already stores the finished text atomically before the
    // reader opens. Keep this device's shelf in sync in memory; do not fetch
    // the whole portal again and do not trigger any extra AI work.
    setSavedStories((current) => {
      const existingIndex = current.findIndex((candidate) => candidate.id === story.id);
      if (existingIndex === -1) return [story, ...current];
      const next = [...current];
      next[existingIndex] = story;
      return next;
    });
  }, [user]);

  const deleteFromLibrary = useCallback(async (id: string) => {
    let deletedStory: Story | undefined;
    setSavedStories((current) => {
      deletedStory = current.find((story) => story.id === id);
      return current.filter((story) => story.id !== id);
    });
    try {
      await DBService.deleteStory(id);
    } catch (e) {
      console.error("Delete error", e);
      if (deletedStory) {
        setSavedStories((current) => current.some((story) => story.id === deletedStory?.id) ? current : [deletedStory!, ...current]);
      }
      showToast(t('deleteStoriesFailed'), 'error');
    }
  }, [showToast, t]);

  const deleteAllStories = async () => {
    setConfirmModal(null);
    try {
      // Each story deletion also removes its private generated image folder on
      // the server. Requests are intentionally sequential so a large library
      // cannot flood the API.
      for (const story of savedStories) await DBService.deleteStory(story.id);
      setSavedStories([]);
      setCurrentStory(null);
      showToast(t('allStoriesDeleted'));
    } catch (error) {
      console.error('Could not delete the whole library:', error);
      showToast(t('deleteStoriesFailed'), 'error');
      await loadLibrary();
    }
  };

  const stopAudio = useCallback(() => {
    playSessionIdRef.current++;
    if (narrationFallbackTimerRef.current) {
      clearTimeout(narrationFallbackTimerRef.current);
      narrationFallbackTimerRef.current = null;
    }
    if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
    }
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop();
        audioSourceRef.current.onended = null;
      } catch (e) {}
      audioSourceRef.current = null;
    }
    setIsPlaying(false);
    setIsNarrationPreparing(false);
    setPreviewVoice(null);
  }, []);

  const playSpeech = useCallback(async (text: string, overrideVoice?: VoiceType) => {
    if (!text) return;
    const currentSessionId = ++playSessionIdRef.current;
    const selectedVoice = overrideVoice || profile.voice;
    const storyLanguage = currentStory?.language || profile.language;
    const nextPageText = currentStory?.pages?.[currentPageIdx + 1]?.content;
    const warmNextPage = () => {
      if (nextPageText) prefetchPageSpeech(nextPageText, selectedVoice, storyLanguage);
    };
    const finishPlayback = () => {
      if (currentSessionId !== playSessionIdRef.current) return;
      setIsPlaying(false);
      setIsNarrationPreparing(false);
      setPreviewVoice(null);
      if (isAutoplay && currentStory && currentPageIdx < (currentStory.pages?.length || 0) - 1) {
        autoplayTimeoutRef.current = setTimeout(() => {
          setCurrentPageIdx(idx => idx + 1);
        }, 1200);
      }
    };
    try {
      if (audioSourceRef.current) {
          try { audioSourceRef.current.stop(); } catch(e){}
          audioSourceRef.current = null;
      }
      if (autoplayTimeoutRef.current) {
          clearTimeout(autoplayTimeoutRef.current);
          autoplayTimeoutRef.current = null;
      }

      if (narrationFallbackTimerRef.current) {
        clearTimeout(narrationFallbackTimerRef.current);
        narrationFallbackTimerRef.current = null;
      }

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      }

      setIsPlaying(true);
      setIsNarrationPreparing(true);
      const narration = await generatePageSpeech(text, selectedVoice, storyLanguage);
      
      if (currentSessionId !== playSessionIdRef.current) return;

      if (!narration?.audio) {
        setIsPlaying(false);
        setIsNarrationPreparing(false);
        return;
      }
      
      const audioData = atob(narration.audio);
      const bytes = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) bytes[i] = audioData.charCodeAt(i);

      let buffer: AudioBuffer;
      if (narration.encoding === 'mp3') {
        const encoded = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
        buffer = await audioContextRef.current.decodeAudioData(encoded);
      } else {
        const sampleRate = narration.sampleRate || 24000;
        buffer = audioContextRef.current.createBuffer(1, bytes.length / 2, sampleRate);
        const dataInt16 = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
        const channelData = buffer.getChannelData(0);
        for (let i = 0; i < channelData.length; i++) channelData[i] = dataInt16[i] / 32768.0;
      }

      const source = audioContextRef.current.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContextRef.current.destination);
      source.onended = finishPlayback;
      audioSourceRef.current = source;
      if (audioContextRef.current.state === 'suspended') await audioContextRef.current.resume();
      source.start(0);
      setIsNarrationPreparing(false);
      warmNextPage();
    } catch (e: any) {
      if (currentSessionId !== playSessionIdRef.current) return;
      setIsPlaying(false);
      setIsNarrationPreparing(false);
      // Device speech is deliberately not used as an automatic fallback. It
      // produced a robotic first sentence and could overlap the real narrator
      // on mobile. The reader stays quiet and the family can tap Play again.
      console.warn("Narration error:", e.message || e);
    }
  }, [profile.voice, profile.language, isAutoplay, currentStory, currentPageIdx]);

  const playVoicePreview = useCallback((voice: VoiceType) => {
    stopAudio();
    const nextProfile = { ...profile, voice };
    setProfile(nextProfile);
    saveProfilePreferences(nextProfile);
    const currentSessionId = ++playSessionIdRef.current;
    setPreviewVoice(voice);
    const name = VOICES.find((candidate) => candidate.id === voice)?.name || 'Lumi';
    const introduction = `${VOICE_INTROS[profile.language]} ${name}.`;
    // A preview uses the same high-quality path as the book. Browser voices
    // are intentionally never mixed into a Lumi story or a voice sample.
    if (currentSessionId === playSessionIdRef.current) void playSpeech(introduction, voice);
  }, [profile, stopAudio, playSpeech, saveProfilePreferences]);

  useEffect(() => {
    // A former page must never start speaking while a fresh book is being
    // prepared. In particular, the reader stays mounted during a transition
    // from an old book to a new one, so this guard prevents a stale effect from
    // narrating the previous story in that short gap.
    if (!isGenerating && appState === 'STORY_BOOK' && currentStory && currentStory.pages && currentStory.pages[currentPageIdx] && !isContinuationLoading) {
      const page = currentStory.pages[currentPageIdx];
      // This only runs after the welcome scene has confirmed that every
      // illustration and every selected narrator page is ready to play.
      playSpeech(page.content);
    }
    return () => stopAudio();
  }, [currentPageIdx, currentStory, playSpeech, stopAudio, appState, isContinuationLoading, isGenerating]);

  useEffect(() => {
    const storyId = currentStory?.id;
    const illustrationsArePending = Boolean(currentStory?.pages.some((page) => page.imagePending));
    if (appState !== 'STORY_BOOK' || !storyId || !illustrationsArePending) return;

    let cancelled = false;
    let checking = false;
    const refreshIllustrations = async () => {
      if (checking) return;
      checking = true;
      try {
        const refreshedStory = await DBService.getStory(storyId);
        if (cancelled) return;
        const visualsChanged = (story?: Story | null) => Boolean(story?.pages.some((page, index) => {
          const freshPage = refreshedStory.pages[index];
          return page.imageUrl !== freshPage?.imageUrl
            || page.imagePending !== freshPage?.imagePending
            || page.imageFailed !== freshPage?.imageFailed;
        }));
        setCurrentStory((current) => current?.id === storyId && visualsChanged(current) ? refreshedStory : current);
        setSavedStories((stories) => stories.map((story) => story.id === storyId && visualsChanged(story) ? refreshedStory : story));
      } catch (error) {
        // The text and immediate narrator remain usable if a connection briefly
        // drops while background illustrations are still being painted.
        console.warn('Illustrationen werden weiter vorbereitet:', error);
      } finally {
        checking = false;
      }
    };

    void refreshIllustrations();
    const interval = window.setInterval(() => void refreshIllustrations(), 900);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [appState, currentStory?.id, currentStory?.pages.map((page) => `${page.imageUrl}:${page.imagePending}:${page.imageFailed}`).join('|')]);

  // Touch handlers for swiping
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndX;

    if (Math.abs(diff) > 50) { // Threshold for swipe
      if (diff > 0) { // Swipe Left -> Next
        if (currentStory && currentPageIdx < currentStory.pages.length - 1) {
          setCurrentPageIdx(idx => idx + 1);
        }
      } else { // Swipe Right -> Prev
        if (currentPageIdx > 0) {
          setCurrentPageIdx(idx => idx - 1);
        }
      }
    }
    touchStartXRef.current = null;
  };

  const toggleReaderFocusMode = () => {
    const nextFocusMode = !readerFocusMode;
    setReaderFocusMode(nextFocusMode);
    setShowLyrics(!nextFocusMode);
    setShowControls(!nextFocusMode);
    setShowReaderFocusHint(false);
  };

  const handleRandomIdea = async () => {
    if (isRollingIdea) return;
    setIsRollingIdea(true);
    try {
      setLocalPrompt(await generateRandomPrompt(profile));
      showToast(t('randomIdeaReady'));
    } catch (error: any) {
      showToast(error.message || 'Die Ideenwolke ist gerade nicht erreichbar.', 'error');
    } finally {
      setIsRollingIdea(false);
    }
  };

  const handlePasswordChange = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordChangeError('');
    setPasswordChangeSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setShowPasswordChange(false);
      showToast(t('passwordChangeConfirmed'));
    } catch (error: any) {
      setPasswordChangeError(localizedError(error));
    } finally {
      setPasswordChangeSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const result = await resendEmailVerification();
      showToast(result.alreadyVerified ? t('emailAlreadyVerified') : t('verificationSent'));
    } catch (error: any) {
      showToast(localizedError(error), 'error');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSupportSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSupportError('');
    setSupportSubmitting(true);
    try {
      const response = await fetch('/api/support', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: supportSubject, message: supportMessage }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Deine Nachricht konnte nicht gesendet werden.');
      setSupportSubject('');
      setSupportMessage('');
      setShowSupport(false);
      showToast('Danke! Deine Nachricht ist beim Lumi-Team angekommen.');
    } catch (error: any) {
      setSupportError(error.message || 'Deine Nachricht konnte nicht gesendet werden.');
    } finally {
      setSupportSubmitting(false);
    }
  };

  const primeNarration = () => {
    // Opening the audio context directly from the Start button keeps iPhone,
    // Android and desktop browsers ready for narration once page one arrives.
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      }
      if (audioContextRef.current.state === 'suspended') void audioContextRef.current.resume();
      // This inaudible, tiny sound unlocks the real narrator on Safari/iOS
      // and Android WebViews while the family is still on the Start button.
      const unlockGain = audioContextRef.current.createGain();
      unlockGain.gain.value = 0.00001;
      const unlockTone = audioContextRef.current.createOscillator();
      unlockTone.connect(unlockGain).connect(audioContextRef.current.destination);
      unlockTone.start();
      unlockTone.stop(audioContextRef.current.currentTime + 0.012);
    } catch {
      // A later Play tap can retry the narration request safely.
    }
  };

  const handleStartWeaving = async (isContinuation = false) => {
    const isOutOfFreeUses = !isPremium && usesRemaining <= 0;
    
    if (!isContinuation && isOutOfFreeUses) {
       setAppState('SUBSCRIPTION');
       return;
    }

    if (profile.photoUri && !photoConsent) {
      showToast(t('photoConsentRequired'), 'error');
      return;
    }

    // Cancel audio before changing the reader state. Calling the audio unlock
    // first could resume a paused utterance from the previous book on Safari.
    stopAudio();
    setReaderFocusMode(false);
    setShowReaderFocusHint(false);
    setShowLyrics(true);
    setShowControls(true);
    setIsGenerating(true);
    if (isContinuation) {
      setIsContinuationLoading(true);
      setShowLyrics(false);
    } else {
      // Remove the former story synchronously from the reader while the new
      // story is generated. It leaves no stale page for the playback effect.
      setCurrentStory(null);
      setCurrentPageIdx(0);
      setAppState('STORY_BOOK');
    }
    setWeavingStep('STORY');
    primeNarration();
    
    try {
      const idea = localPrompt || t('defaultAdventure');
      const storyProfile = { ...profile };
      const usedPhotoReference = Boolean(storyProfile.photoUri);

      const canProceed = await decrementUsage();
      if (!canProceed && !isPremium) {
        setAppState('SUBSCRIPTION');
        setIsGenerating(false);
        setIsContinuationLoading(false);
        return;
      }

      // Safety is authoritative in /api/stories. We deliberately do not make
      // the same child wait for a second, client-side AI safety call first.
      const generatedStory = await generateStoryBook(storyProfile, idea, isPremium ? 'premium' : 'free', isContinuation ? currentStory! : undefined);
      generatedStory.authorId = user?.uid || 'guest';
      setWeavingStep('PAINTING');
      const story = await waitForFinishedBook(generatedStory, storyProfile.voice, storyProfile.language);
      void persistChildProfile();
      
      if (isContinuation && currentStory) {
        // The server has already saved this exact continuation atomically.
        // Refresh the shelf in the background so reading never waits on it.
        void saveToLibrary(story);
        setCurrentStory(story);
        setCurrentPageIdx(currentStory.pages.length);
        
        setTimeout(() => {
            setIsGenerating(false);
            setIsContinuationLoading(false);
            setShowLyrics(true);
        }, 100);
      } else {
        story.isPaid = true; 
        void saveToLibrary(story);
        setCurrentStory(story);
        setCurrentPageIdx(0);
        // The welcome scene has already waited for the complete book and its
        // selected narrator, so the first page can start cleanly now.
        setShowLyrics(true);
        setShowControls(true);
        setIsGenerating(false);
      }
      await refreshUser();
      
      if (usedPhotoReference && !rememberPhoto) {
        clearStoryPhoto();
      }
      showToast(usedPhotoReference && !rememberPhoto ? t('photoDiscarded') : t('success'), 'success');
    } catch (e: any) {
      console.error("Weaving error:", e);
      if (e?.payload?.subscriptionRequired || e?.status === 402) {
        setAppState('SUBSCRIPTION');
      } else if (e?.payload?.safetyBlocked || e?.status === 422) {
        setSafetyAlarm(true);
        if (!isContinuation) setAppState('CREATE_STORY');
      } else if (!safetyAlarm) {
        showToast(e.message || t('error'), 'error');
        if (!isContinuation) setAppState('CREATE_STORY');
      }
      setIsGenerating(false);
      setIsContinuationLoading(false);
    }
  };

  const handleUpgrade = async (plan: SubscriptionPlan) => {
    setPaymentProcessing(true);
    try {
      await upgrade(plan);
    } catch (error: any) {
      showToast(error.message || 'Stripe konnte nicht geöffnet werden.', 'error');
      setPaymentProcessing(false);
    }
  };

  const handleBillingPortal = async () => {
    setPaymentProcessing(true);
    try {
      await openBillingPortal();
    } catch (error: any) {
      showToast(error.message || 'Das Abo-Portal konnte nicht geöffnet werden.', 'error');
      setPaymentProcessing(false);
    }
  };

  const handlePrintBookCheckout = async (story: Story) => {
    setPaymentProcessing(true);
    try {
      const url = await DBService.startPrintBookCheckout(story.id);
      window.location.assign(url);
    } catch (error: any) {
      showToast(error.message || 'Die Buchbestellung konnte nicht geöffnet werden.', 'error');
      setPaymentProcessing(false);
    }
  };

  const handleLogout = async () => {
    setConfirmModal(null);
    stopAudio();
    await logout();
    setAppState('AUTH_GATE');
    showToast(t('logout'));
  };

  const handleDeleteAccount = async () => {
    setConfirmModal(null);
    stopAudio();
    if (user) {
      const stories = await DBService.getAllStories(user.uid);
      for (const s of stories) {
        await DBService.deleteStory(s.id);
      }
      await removeSavedPhotoReference(user.uid).catch(() => undefined);
    }
    await deleteAccount();
    setAppState('AUTH_GATE');
  };

  const validatePhoto = (file: File) => {
    // iPhones commonly use HEIC. Safari can convert it to the compact JPEG
    // request copy; browsers that cannot read it show a clear, actionable
    // message in optimisePhotoForStory instead of a cryptic file error.
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type)) throw new Error('Bitte wähle ein JPG, PNG, WebP oder HEIC-Foto aus.');
    if (file.size > 8 * 1024 * 1024) throw new Error(t('photoTooLarge'));
  };

  const handleProfilePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      validatePhoto(file);
      // Profile pictures never leave the browser, so preparing a compact copy
      // is faster and avoids an unnecessary remote moderation round-trip.
      const photoURL = await optimisePhotoForStory(file, 720, 350_000);
      await updateAuthProfile({ photoURL });
      showToast(t('profileUpdated'));
    } catch (error: any) {
      showToast(localizedError(error, 'photoCheckFailed'), 'error');
    } finally {
      e.target.value = '';
    }
  };

  const handleRemoveProfilePhoto = async () => {
    try {
      await updateAuthProfile({ photoURL: undefined });
      showToast(t('photoRemove'));
    } catch (error: any) {
      showToast(localizedError(error), 'error');
    }
  };

  const handleMarketingConsentChange = async (enabled: boolean) => {
    try {
      await updateAuthProfile({ marketingConsent: enabled });
      showToast(enabled ? 'Lumi-Neuigkeiten sind aktiviert.' : 'Lumi-Neuigkeiten sind deaktiviert.');
    } catch (error: any) {
      showToast(localizedError(error), 'error');
    }
  };

  const handleStoryPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      validatePhoto(file);
      setIsPhotoPreparing(true);
      const photoUri = await optimisePhotoForStory(file);
      // Choosing a new child photo must not silently keep the previous child
      // reference around on this device.
      if (user?.uid) await removeSavedPhotoReference(user.uid).catch(() => undefined);
      setProfile((current) => ({ ...current, photoUri }));
      setRememberPhoto(false);
      setPhotoConsent(false);
      showToast(t('photoReady'));
    } catch (error: any) {
      showToast(localizedError(error, 'photoCheckFailed'), 'error');
    } finally {
      setIsPhotoPreparing(false);
      e.target.value = '';
    }
  };

  const handleRememberPhoto = async (remember: boolean) => {
    if (!user?.uid || !profile.photoUri) {
      showToast(t('photoCheckFailed'), 'error');
      return;
    }
    // A preference toggle should acknowledge the tap immediately. If storage
    // is unavailable, it rolls back with a useful explanation instead of
    // appearing to ignore the person’s tap.
    const previousValue = rememberPhoto;
    setRememberPhoto(remember);
    setIsRememberingPhoto(true);
    try {
      if (remember) {
        await savePhotoReference(user.uid, profile.photoUri);
        showToast(privatePhotoCopy.saved);
      } else {
        await removeSavedPhotoReference(user.uid);
      }
    } catch (error: any) {
      setRememberPhoto(previousValue);
      showToast('Dieses Gerät hat den privaten Foto-Speicher blockiert. Bitte erlaube Website-Daten für Lumi und versuche es erneut.', 'error');
    } finally {
      setIsRememberingPhoto(false);
    }
  };

  const clearStoryPhoto = () => {
    setProfile((current) => ({ ...current, photoUri: undefined }));
    setPhotoConsent(false);
    setRememberPhoto(false);
    if (user?.uid) void removeSavedPhotoReference(user.uid).catch(() => undefined);
    const photoInput = document.getElementById('photo-input') as HTMLInputElement | null;
    if (photoInput) photoInput.value = '';
  };

  const updateChildName = (name: string) => {
    const nextProfile = { ...profile, name };
    setProfile(nextProfile);
    saveProfilePreferences(nextProfile);
  };

  const persistChildProfile = async () => {
    const nextProfile = { ...profile, name: profile.name.trim() };
    setProfile(nextProfile);
    saveProfilePreferences(nextProfile);
    if (nextProfile.name.length < 2) return;
    try {
      await DBService.saveChildProfile(nextProfile);
    } catch (error) {
      // The local profile remains usable immediately, even during a temporary
      // network interruption. It will be retried on the next edit/start.
      console.warn('Kindprofil konnte nicht synchronisiert werden:', error);
    }
  };

  const renderScreen = () => {
    if (authLoading && appState !== 'SPLASH') {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full gap-8">
           <div className="w-20 h-20 animate-pulse"><MemoizedMoon /></div>
           <p className="text-[10px] uppercase tracking-[0.5em] opacity-40">...</p>
        </div>
      );
    }

    switch(appState) {
      case 'SPLASH':
        return (
          <div className="flex flex-col items-center justify-center gap-6 animate-in fade-in duration-1000 h-full w-full p-10">
            <div className="w-40 h-40 drop-shadow-[0_0_60px_rgba(255,255,255,0.8)]"><MemoizedMoon /></div>
            <h1 className="text-6xl lumi-logo-font magical-glow">Lumi</h1>
          </div>
        );

      case 'AUTH_GATE':
        const landingCopy = LANDING_PREVIEW_COPY[profile.language] || LANDING_PREVIEW_COPY.en;
        const landingExample = LANDING_EXAMPLE_COPY[profile.language] || LANDING_EXAMPLE_COPY.en;
        return (
          <div className="auth-scroll-container screen-in" dir={profile.language === 'ar' ? 'rtl' : undefined}>
            <main className="mx-auto flex min-h-full w-full max-w-6xl flex-col px-5 pb-[calc(3.5rem+var(--safe-bottom))] pt-[calc(1.5rem+var(--safe-top))] sm:px-8 lg:px-12">
              <header className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 shrink-0 drop-shadow-[0_0_26px_rgba(255,255,255,0.45)]"><MemoizedMoon /></div>
                  <div>
                    <h1 className="text-3xl leading-none lumi-logo-font magical-glow">Lumi</h1>
                    <p className="mt-1 text-[8px] font-black uppercase tracking-[0.28em] text-white/45">{t('homeGreeting')}</p>
                  </div>
                </div>
                <button onClick={() => { clearAuthFeedback(); setAppState('LOGIN'); }} className="shrink-0 rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-[9px] font-black uppercase tracking-[0.14em] text-white shadow-professional transition active:scale-95">
                  {t('loginEmail')}
                </button>
              </header>

              <section className="grid items-center gap-8 py-10 sm:py-14 lg:grid-cols-[1.05fr_.95fr] lg:gap-14 lg:py-16">
                <div className="max-w-xl text-center lg:text-start">
                  <p className="mb-4 text-[10px] font-black uppercase tracking-[0.2em] text-amber-100/90">✦ {landingCopy.eyebrow}</p>
                  <h2 className="text-4xl leading-[1.03] font-bold dream-font text-white sm:text-5xl lg:text-6xl magical-glow">{landingCopy.heading}</h2>
                  <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-white/65 lg:mx-0 sm:text-base">{landingCopy.description}</p>
                  <div className="mx-auto mt-8 grid max-w-md gap-3 sm:grid-cols-2 lg:mx-0">
                    <button onClick={() => { clearAuthFeedback(); setProfile((current) => ({ ...current, language: detectSuggestedLanguage() })); setAppState('REGISTER'); }} className="min-h-14 rounded-[22px] bg-white px-5 text-[10px] font-black uppercase tracking-[0.16em] text-slate-950 shadow-professional transition active:scale-95 sm:text-[11px]">
                      {landingCopy.start}
                    </button>
                    <button onClick={() => { clearAuthFeedback(); setAppState('LOGIN'); }} className="min-h-14 rounded-[22px] border border-white/20 bg-white/5 px-5 text-[10px] font-black uppercase tracking-[0.16em] text-white transition active:scale-95 sm:text-[11px]">
                      {t('loginEmail')}
                    </button>
                  </div>
                  {authError && <p role="alert" className="mt-5 rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-xs leading-relaxed text-red-100">{authError}</p>}
                </div>

                <aside aria-label={landingExample.kicker} className="relative mx-auto w-full max-w-[500px] overflow-hidden rounded-[34px] border border-white/20 bg-[#161a3b]/90 p-4 shadow-[0_24px_90px_rgba(0,0,0,0.42)] sm:p-5">
                  <div className="pointer-events-none absolute -right-16 -top-14 h-40 w-40 rounded-full bg-amber-200/20 blur-3xl" />
                  <div className="relative flex items-center justify-between gap-4 border-b border-white/10 pb-4">
                    <div className="flex items-center gap-3"><div className="h-9 w-9 rounded-xl bg-amber-100/15 p-1.5"><MemoizedMoon /></div><span className="text-sm font-bold dream-font">Lumi</span></div>
                    <span className="rounded-full border border-emerald-200/25 bg-emerald-200/10 px-3 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-emerald-50">{landingExample.kicker}</span>
                  </div>
                  <div className="relative mt-5 grid grid-cols-[minmax(0,.82fr)_22px_minmax(0,1fr)] items-center gap-2 sm:grid-cols-[minmax(0,.82fr)_34px_minmax(0,1fr)] sm:gap-3">
                    <figure className="min-w-0">
                      <div className="overflow-hidden rounded-[22px] border border-white/20 bg-slate-950/30"><img src="/assets/lumi-example-child-reference.png" alt={`${landingExample.source}: ${landingExample.note}`} className="aspect-[4/5] w-full object-cover" /></div>
                      <figcaption className="mt-2 text-center text-[9px] font-black uppercase tracking-[0.12em] text-white/50">{landingExample.source}</figcaption>
                    </figure>
                    <span aria-hidden="true" className="text-center text-xl font-bold text-amber-100">→</span>
                    <figure className="min-w-0">
                      <div className="overflow-hidden rounded-[22px] border border-amber-100/30 bg-slate-950/30"><img src="/assets/lumi-example-child-storybook.png" alt={landingExample.result} className="aspect-[4/5] w-full object-cover" /></div>
                      <figcaption className="mt-2 text-center text-[9px] font-black uppercase tracking-[0.12em] text-amber-100/85">{landingExample.result}</figcaption>
                    </figure>
                  </div>
                  <div className="relative mt-4 rounded-[22px] border border-emerald-100/20 bg-emerald-100/[0.07] p-4">
                    <p className="text-lg font-bold leading-tight dream-font text-white">{landingExample.heading}</p>
                    <p className="mt-2 text-xs leading-relaxed text-white/70">{landingExample.description}</p>
                  </div>
                  {landingExample.note ? <p className="relative mt-4 flex items-start gap-2 text-[10px] leading-relaxed text-white/50"><IconStar className="mt-0.5 h-4 w-4 shrink-0 text-amber-100" />{landingExample.note}</p> : null}
                </aside>
              </section>

              <section aria-labelledby="lumi-preview-heading" className="pb-9 sm:pb-12">
                <div className="mx-auto max-w-2xl text-center">
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-amber-100">✦ Lumi erleben</p>
                  <h2 id="lumi-preview-heading" className="mt-3 text-3xl font-bold dream-font sm:text-4xl">{landingCopy.previewHeading}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{landingCopy.previewDescription}</p>
                </div>

                <div className="mt-8 grid gap-4 md:grid-cols-3 lg:gap-5">
                  <article className="overflow-hidden rounded-[30px] border border-white/15 bg-white/[0.055] shadow-professional">
                    <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.04] px-5 py-4"><span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/45">01</span><IconMagicWand className="h-5 w-5 text-amber-100" /></div>
                    <div className="p-5"><h3 className="text-xl font-bold dream-font">{landingCopy.steps[0]}</h3><div className="mt-5 rounded-[20px] border border-white/10 bg-slate-950/35 p-4 text-sm text-white/80">{landingCopy.wish}</div><div className="mt-3 flex items-center gap-2 text-[10px] text-white/45"><IconDice className="h-4 w-4" />{t('randomIdeaHint')}</div></div>
                  </article>
                  <article className="overflow-hidden rounded-[30px] border border-white/15 bg-white/[0.055] shadow-professional">
                    <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.04] px-5 py-4"><span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/45">02</span><IconCamera className="h-5 w-5 text-cyan-100" /></div>
                    <div className="p-5"><h3 className="text-xl font-bold dream-font">{landingCopy.steps[1]}</h3><div className="mt-5 grid grid-cols-[1fr_18px_1fr] items-center gap-2"><img src="/assets/lumi-example-child-reference.png" alt={landingExample.source} className="aspect-square w-full rounded-2xl object-cover" /><span aria-hidden="true" className="text-center text-amber-100">→</span><img src="/assets/lumi-example-child-storybook.png" alt={landingExample.result} className="aspect-square w-full rounded-2xl object-cover" /></div><p className="mt-3 text-[10px] leading-relaxed text-white/55">{landingExample.private}</p></div>
                  </article>
                  <article className="overflow-hidden rounded-[30px] border border-white/15 bg-white/[0.055] shadow-professional">
                    <div className="relative h-32 overflow-hidden border-b border-white/10"><img src="/assets/lumi-portal.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-55" /><div className="absolute inset-0 bg-gradient-to-t from-[#161a3b] via-transparent to-transparent" /><div className="absolute bottom-3 left-4 flex items-center gap-2"><IconBook className="h-4 w-4 text-amber-100" /><span className="text-[10px] font-black text-white">1 / 6</span></div></div>
                    <div className="p-5"><h3 className="text-xl font-bold dream-font">{landingCopy.steps[2]}</h3><p className="mt-4 text-sm leading-relaxed text-white/65">{landingCopy.book}</p><div className="mt-4 flex items-center gap-2 text-[10px] text-white/55"><IconStar className="h-4 w-4 text-amber-100" />Digital lesen oder später als echtes Buch verschenken.</div></div>
                  </article>
                </div>
              </section>

              <section aria-labelledby="lumi-video-title" className="mb-10 grid items-center gap-6 rounded-[32px] border border-amber-100/20 bg-white/[0.055] p-5 shadow-professional sm:p-7 lg:grid-cols-[1fr_280px]">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-amber-100">✦ Ein Blick ins Traumland</p>
                  <h2 id="lumi-video-title" className="mt-3 text-3xl font-bold dream-font sm:text-4xl">So fühlt sich eure Geschichte an.</h2>
                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/65">Echte Erzählerstimme, sanfte Musik und eine Welt, die nur für euch entsteht – in wenigen Minuten aus eurer Idee.</p>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[10px] text-white/70"><span>✓ Persönlich</span><span>✓ Mehrsprachig</span><span>✓ Sicher & privat</span></div>
                </div>
                <div className="overflow-hidden rounded-[24px] border border-white/20 bg-black/30"><video className="block aspect-[9/16] w-full object-cover" controls preload="metadata" poster="/assets/lumi-portal.jpg"><source src="/assets/lumi-de.mp4" type="video/mp4" /></video></div>
              </section>

              <section className="mb-4 rounded-[28px] border border-amber-100/20 bg-amber-100/[0.07] p-5 text-center shadow-professional sm:flex sm:items-center sm:justify-between sm:gap-6 sm:text-start">
                <div><h2 className="text-xl font-bold dream-font">Eine Geschichte, die bleibt</h2><p className="mt-2 text-xs leading-relaxed text-white/70">Lest euer Abenteuer sofort digital. Wenn es euer Lieblingsbuch wird, könnt ihr es später als gedrucktes Geschenk bestellen.</p></div>
                <button onClick={() => { clearAuthFeedback(); setProfile((current) => ({ ...current, language: detectSuggestedLanguage() })); setAppState('REGISTER'); }} className="mt-4 w-full shrink-0 rounded-2xl bg-white px-5 py-4 text-[10px] font-black uppercase tracking-[0.14em] text-slate-950 shadow-professional transition active:scale-95 sm:mt-0 sm:w-auto">{t('signup')}</button>
              </section>
            </main>
          </div>
        );

      case 'LOGIN':
      case 'REGISTER':
        return (
          <div className="auth-scroll-container screen-in">
            <div className="flex min-h-full max-w-sm w-full flex-col px-10 pb-[calc(2rem+var(--safe-bottom))] mx-auto safe-padding-top">
              <header className="flex items-center gap-6 py-8">
                <button onClick={() => setAppState('AUTH_GATE')} className="w-10 h-10 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center active:scale-90 transition-all shadow-professional"><IconBack className="w-4 h-4" /></button>
                <h2 className="text-xl font-bold dream-font">{appState === 'LOGIN' ? t('loginTitle') : t('signup')}</h2>
              </header>
              <form onSubmit={handleAuthSubmit} className="space-y-5 mt-8" noValidate>
              {appState === 'REGISTER' && (
                <>
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black uppercase tracking-[0.25em] text-white/50">{t('firstName')}</span>
                    <input type="text" value={authName} onChange={(event) => setAuthName(event.target.value)} autoComplete="name" className="lumi-input text-base py-4" required minLength={2} />
                  </label>
                  <fieldset className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <legend className="px-1 text-[10px] font-black uppercase tracking-[0.25em] text-white/50">{t('language')}</legend>
                    <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup" aria-label={t('language')}>
                      {LANGUAGES.map((language) => {
                        const selected = profile.language === language.code;
                        return (
                          <button
                            type="button"
                            key={language.code}
                            role="radio"
                            aria-checked={selected}
                            onClick={() => setProfile((current) => ({ ...current, language: language.code as LanguageCode }))}
                            className={`min-h-12 rounded-xl border px-2 py-2 text-[10px] font-bold transition active:scale-95 ${selected ? 'border-emerald-300 bg-emerald-300/15 text-emerald-50' : 'border-white/10 bg-black/10 text-white/70'}`}
                          >
                            <span className="mr-1" aria-hidden="true">{language.flag}</span>{language.label}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                </>
              )}
              <label className="block space-y-2">
                <span className="text-[10px] font-black uppercase tracking-[0.25em] text-white/50">{t('email')}</span>
                <input type="email" value={authEmail} onChange={(event) => setAuthEmail(event.target.value)} autoComplete="email" className="lumi-input text-base py-4" required />
              </label>
              <label className="block space-y-2">
                <span className="text-[10px] font-black uppercase tracking-[0.25em] text-white/50">{t('password')}</span>
                <input type="password" value={authPassword} onChange={(event) => setAuthPassword(event.target.value)} autoComplete={appState === 'LOGIN' ? 'current-password' : 'new-password'} className="lumi-input text-base py-4" required minLength={10} />
                {appState === 'REGISTER' && <span className="block text-[9px] text-white/35">{t('minPasswordLength')}</span>}
              </label>
              {appState === 'REGISTER' && (
                <div className="space-y-3">
                  <label className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-[11px] leading-relaxed text-white/70">
                    <input type="checkbox" checked={adultConfirmed} onChange={(event) => setAdultConfirmed(event.target.checked)} className="mt-0.5 h-4 w-4 accent-indigo-400" required />
                    <span>{t('adultConfirmation')}</span>
                  </label>
                  <label className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.035] p-4 text-[11px] leading-relaxed text-white/60">
                    <input type="checkbox" checked={marketingConsent} onChange={(event) => setMarketingConsent(event.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-300" />
                    <span>{MARKETING_CONSENT_COPY[profile.language].registration}</span>
                  </label>
                </div>
              )}
              {authError && <p role="alert" className="rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-xs text-red-100">{authError}</p>}
              {authMessage && <p role="status" className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-xs text-emerald-100">{authMessage}</p>}
              <button type="submit" disabled={authSubmitting || (appState === 'REGISTER' && !adultConfirmed)} className="w-full py-5 bg-white/10 border border-white/20 rounded-[25px] font-bold uppercase tracking-[0.3em] shadow-professional active:scale-95 transition-all magical-glow disabled:opacity-40">
                {authSubmitting ? t('pleaseWait') : (appState === 'LOGIN' ? t('loginTitle') : t('createAccount'))}
              </button>
              {appState === 'LOGIN' && <button type="button" onClick={() => { clearAuthFeedback(); setPasswordResetToken(null); setAppState('FORGOT_PASSWORD'); }} className="w-full py-2 text-[10px] font-bold text-indigo-300">{t('forgotPass')}</button>}
              <button type="button" onClick={() => { clearAuthFeedback(); setAppState(appState === 'LOGIN' ? 'REGISTER' : 'LOGIN'); }} className="w-full rounded-2xl border border-white/10 py-3 text-[11px] font-bold text-white/70">
                {appState === 'LOGIN' ? 'Noch kein Konto? Kostenlos registrieren' : 'Schon ein Konto? Anmelden'}
              </button>
              </form>
            </div>
          </div>
        );

      case 'FORGOT_PASSWORD':
        return (
          <div className="auth-scroll-container screen-in">
            <div className="mx-auto flex min-h-full max-w-sm w-full flex-col px-6 pb-[calc(2rem+var(--safe-bottom))] sm:px-10 safe-padding-top">
              <header className="flex items-center gap-6 py-8">
                <button onClick={() => { clearAuthFeedback(); setAppState('LOGIN'); }} className="w-10 h-10 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center active:scale-90 transition-all shadow-professional"><IconBack className="w-4 h-4" /></button>
                <h2 className="text-xl font-bold dream-font">{passwordResetToken ? t('newPassword') : t('resetPassword')}</h2>
              </header>
              <form onSubmit={handlePasswordAction} className="mt-8 space-y-5">
                {passwordResetToken ? (
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black uppercase tracking-[0.25em] text-white/50">{t('newPassword')}</span>
                    <input type="password" value={authPassword} onChange={(event) => setAuthPassword(event.target.value)} autoComplete="new-password" minLength={10} className="lumi-input text-base py-4" required />
                  </label>
                ) : (
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black uppercase tracking-[0.25em] text-white/50">{t('email')}</span>
                    <input type="email" value={authEmail} onChange={(event) => setAuthEmail(event.target.value)} autoComplete="email" className="lumi-input text-base py-4" required />
                  </label>
                )}
                {authError && <p role="alert" className="rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-xs text-red-100">{authError}</p>}
                {authMessage && <p role="status" className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-xs text-emerald-100">{authMessage}</p>}
                <button type="submit" disabled={authSubmitting} className="w-full py-5 bg-white text-slate-950 rounded-[25px] font-black uppercase tracking-[0.2em] shadow-professional disabled:opacity-40">
                  {authSubmitting ? t('pleaseWait') : (passwordResetToken ? t('changePassword') : t('sendEmail'))}
                </button>
              </form>
            </div>
          </div>
        );

      case 'ONBOARDING':
        return (
          <div className="auth-scroll-container screen-in">
            <div className="mx-auto flex min-h-full max-w-sm w-full flex-col items-center justify-center px-6 py-10 sm:px-10 safe-padding-top safe-padding-bottom">
               <h2 className="mb-10 text-center text-3xl font-bold dream-font magical-glow">{t('onboardingTitle')}</h2>
               <div className="w-full space-y-6">
                  <input 
                     value={profile.name} 
                     onChange={e => setProfile({...profile, name: e.target.value})} 
                     className="lumi-input h-20 text-center text-2xl font-bold dream-font shadow-professional" 
                     placeholder={t('onboardingName')} 
                  />
                  
                  <div className="rounded-[30px] border border-white/10 bg-white/5 p-8 text-center shadow-professional">
                     <p className="mb-4 text-[10px] font-black uppercase tracking-[0.5em] text-white/20">{t('onboardingAge')}: {profile.age}</p>
                     <input type="range" min="2" max="10" value={profile.age} onChange={e => setProfile({...profile, age: parseInt(e.target.value)})} className="h-1 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-white" />
                  </div>
                  
               <button onClick={async () => { if (!user) return; await DBService.saveChildProfile(profile); saveProfilePreferences(profile); setAppState('HOME'); }} disabled={!profile.name.trim()} className="w-full py-5 bg-white/10 border border-white/20 rounded-[30px] font-black uppercase tracking-[0.3em] shadow-professional active:scale-95 transition-all disabled:opacity-30 magical-glow">
                     {t('startNow')}
                  </button>
               </div>
            </div>
          </div>
        );

      case 'HOME':
        return (
          <div className="w-full h-full flex flex-col items-center px-8 screen-in safe-padding-top scroll-container">
            <header className="w-full flex justify-between items-center py-4 mb-8 shrink-0">
               <button onClick={() => setAppState('ACCOUNT')} className="w-12 h-12 bg-white/10 border border-white/20 rounded-2xl flex items-center justify-center active:scale-90 transition-all shadow-professional overflow-hidden">
                  {user?.photoURL ? <img src={user.photoURL} className="w-full h-full object-cover" alt="Profile" /> : <IconUser className="w-6 h-6 opacity-30" />}
               </button>
               <div className="w-12" aria-hidden="true" />
            </header>

            <div className="text-center mb-10 shrink-0">
              <h2 className="text-5xl handwritten magical-glow mb-3">{t('homeTitle')} {profile.name}!</h2>
              <p className="text-[8px] text-white/30 uppercase tracking-[0.5em]">{t('homeGreeting').toUpperCase()}</p>
            </div>

            <div className="w-full max-w-[320px] flex-1 flex flex-col justify-center space-y-5 pb-24">
              <button onClick={() => setAppState('CREATE_STORY')} className="w-full py-12 bg-gradient-to-br from-white/10 to-indigo-600/5 border border-white/20 rounded-[40px] flex flex-col items-center justify-center gap-4 shadow-professional group active:scale-[0.98] transition-all">
                <div className="text-6xl group-hover:scale-110 transition-transform duration-700 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">✨</div>
                <span className="text-xs font-black uppercase tracking-[0.3em] text-white/80">{t('startStory').toUpperCase()}</span>
                {!isPremium && <div className="text-[8px] uppercase tracking-widest text-white/30">{usesRemaining > 0 ? `${usesRemaining} ${t('credits')}` : t('noEnergy')}</div>}
              </button>
              
              <div className="grid grid-cols-2 gap-5">
                <button onClick={() => setAppState('LIBRARY')} className="p-6 bg-white/5 border border-white/10 rounded-[30px] flex flex-col items-center gap-3 active:scale-95 transition-all">
                  <IconBook className="w-6 h-6 opacity-60" />
                  <span className="text-[8px] font-black uppercase tracking-[0.2em] opacity-40">{t('myStories').toUpperCase()}</span>
                </button>
                <button onClick={() => setAppState('SUBSCRIPTION')} className="p-6 bg-white/5 border border-white/10 rounded-[30px] flex flex-col items-center gap-3 active:scale-95 transition-all relative overflow-hidden">
                  {!isPremium && <div className="absolute top-2 right-2 px-2 py-0.5 bg-white text-black text-[6px] font-black rounded-full">Pro</div>}
                  <IconStar className="w-6 h-6 opacity-60 text-amber-400" />
                  <span className="text-[8px] font-black uppercase tracking-[0.2em] opacity-40">{isPremium ? 'PREMIUM' : t('upgrade').toUpperCase()}</span>
                </button>
              </div>
            </div>
          </div>
        );

      case 'CREATE_STORY':
        return (
          <div className="w-full h-full flex flex-col px-8 screen-in safe-padding-top scroll-container">
             <header className="flex justify-between items-center py-4 shrink-0">
                <button onClick={() => setAppState('HOME')} className="w-10 h-10 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center active:scale-90 transition-all shadow-professional"><IconBack className="w-4 h-4 opacity-80" /></button>
                <h2 className="text-xl font-bold dream-font">{t('createTitle')}</h2>
                <button onClick={() => void handleRandomIdea()} disabled={isRollingIdea} aria-label={t('randomIdea')} className="w-10 h-10 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center active:scale-90 transition-all shadow-professional disabled:opacity-50"><IconDice className={`w-4 h-4 opacity-80 ${isRollingIdea ? 'animate-spin' : ''}`} /></button>
             </header>

             <div className="flex-1 flex flex-col gap-6 mt-4 pb-24">
                <label className="rounded-[22px] border border-white/10 bg-white/5 px-5 py-3 shadow-professional">
                  <span className="mb-1 block text-[8px] font-black uppercase tracking-[0.28em] text-white/35">{t('onboardingName')}</span>
                  <input
                    value={profile.name}
                    onChange={(event) => updateChildName(event.target.value)}
                    onBlur={() => void persistChildProfile()}
                    maxLength={30}
                    className="w-full bg-transparent text-lg font-bold dream-font outline-none placeholder:text-white/25"
                    placeholder={t('onboardingName')}
                    aria-label={t('onboardingName')}
                  />
                </label>
                <textarea 
                  value={localPrompt} 
                  onChange={e => setLocalPrompt(e.target.value)}
                  className="w-full flex-1 bg-white/5 border border-white/10 rounded-[30px] p-6 text-xl font-light dream-font resize-none focus:bg-white/10 transition-all shadow-professional min-h-[200px]"
                  placeholder={t('placeholder')}
                />

                <section className="overflow-hidden rounded-[22px] border border-cyan-200/20 bg-cyan-200/[0.06] shadow-professional">
                  <button
                    type="button"
                    aria-expanded={showHowItWorks}
                    onClick={() => setShowHowItWorks((open) => !open)}
                    className="flex min-h-[58px] w-full items-center gap-3 px-4 text-start transition active:scale-[0.99]"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cyan-100/15 text-cyan-50"><IconMagicWand className="h-4 w-4" /></span>
                    <span className="flex-1 text-[10px] font-black uppercase tracking-[0.16em] text-cyan-50">{howItWorksCopy.title}</span>
                    <IconBack className={`h-4 w-4 transition-transform ${showHowItWorks ? '-rotate-90' : 'rotate-180'}`} />
                  </button>
                  {showHowItWorks && (
                    <div className="border-t border-cyan-100/10 px-4 pb-4 pt-3 text-start animate-in fade-in slide-in-from-top-1">
                      <p className="mb-3 text-[11px] leading-relaxed text-white/75">{howItWorksCopy.intro}</p>
                      <ol className="space-y-2.5">
                        {[howItWorksCopy.story, howItWorksCopy.photo, howItWorksCopy.voice, howItWorksCopy.saved].map((text, index) => (
                          <li key={text} className="flex items-start gap-2.5 text-[10px] leading-relaxed text-white/85">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-100/20 text-[9px] font-black text-cyan-50">{index + 1}</span>
                            <span>{text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </section>

                <button onClick={() => void handleRandomIdea()} disabled={isRollingIdea} className="w-full rounded-[22px] border border-amber-200/20 bg-gradient-to-r from-amber-200/10 to-indigo-300/10 p-4 flex items-center gap-4 text-left active:scale-[0.98] transition-all disabled:opacity-50">
                  <span className={`w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0 ${isRollingIdea ? 'animate-spin' : ''}`}><IconDice className="w-5 h-5 text-amber-100" /></span>
                  <span><span className="block text-[10px] font-black uppercase tracking-[0.2em] text-amber-100">{t('randomIdea')}</span><span className="block mt-1 text-[11px] text-white/60">{t('randomIdeaHint')}</span></span>
                </button>
                
                <div className="grid grid-cols-2 gap-3 shrink-0">
                   <button onClick={() => { stopAudio(); setShowSettings(true); }} className="bg-white/5 border border-white/10 p-4 rounded-[20px] flex items-center gap-3 active:scale-95 transition-all">
                      <IconMic className="w-4 h-4 opacity-60" />
                      <div className="text-left"><p className="text-[7px] uppercase font-black opacity-30">{t('voice')}</p><p className="text-[11px] font-bold">{t(VOICES.find(v => v.id === profile.voice)?.descKey || 'voice_kind')}</p></div>
                   </button>
                   
                   <div className="relative">
                      <input type="file" id="photo-input" className="sr-only" disabled={isPhotoPreparing} accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={handleStoryPhotoChange} />
                      <label htmlFor="photo-input" aria-disabled={isPhotoPreparing} className={`w-full bg-white/5 border border-white/10 p-4 rounded-[20px] flex items-center gap-3 transition-all overflow-hidden relative ${isPhotoPreparing ? 'cursor-wait opacity-50' : 'cursor-pointer active:scale-95'}`}>
                         {profile.photoUri ? <img src={profile.photoUri} className="absolute inset-0 w-full h-full object-cover opacity-20" alt="Added" /> : <IconCamera className="w-4 h-4 opacity-60" />}
                         <div className="text-left z-10"><p className="text-[7px] uppercase font-black opacity-30">{t('photo')}</p><p className="text-[11px] font-bold">{isPhotoPreparing ? privatePhotoCopy.preparing : profile.photoUri ? t('active') : t('off')}</p></div>
                      </label>
                      {profile.photoUri && (
                        <button aria-label={t('photoRemove')} onClick={(e) => { e.stopPropagation(); clearStoryPhoto(); }} className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all z-20">
                          <IconTrash className="w-3.5 h-3.5" />
                        </button>
                      )}
                   </div>
                </div>

                {profile.photoUri && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 rounded-[22px] border border-cyan-300/25 bg-cyan-300/[0.07] p-3 text-left" aria-live="polite">
                      <img src={profile.photoUri} alt="" className="h-14 w-14 shrink-0 rounded-xl border border-white/20 object-cover" />
                      <p className="text-[10px] leading-relaxed text-cyan-50/90"><strong className="mb-1 block text-white">{t('photoReady')}</strong>{t('photoReference')}</p>
                    </div>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={photoConsent}
                      onClick={() => setPhotoConsent((current) => !current)}
                      className={`flex min-h-[108px] w-full items-center gap-4 rounded-[22px] border p-4 text-left text-[10px] leading-relaxed transition active:scale-[0.99] ${photoConsent ? 'border-emerald-300/60 bg-emerald-400/15 text-emerald-50' : 'border-amber-200/35 bg-amber-200/[0.08] text-white/80'}`}
                    >
                      <span aria-hidden="true" className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border transition ${photoConsent ? 'border-emerald-200 bg-emerald-400 text-slate-950' : 'border-white/35 bg-white/10 text-white/80'}`}>
                        {photoConsent ? <IconCheck className="h-6 w-6" /> : <span className="h-5 w-5 rounded-md border-2 border-current" />}
                      </span>
                      <span><strong className="mb-1 block text-sm text-white">{t('photoReference')}</strong>{t('photoConsent')}</span>
                    </button>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={rememberPhoto}
                      onClick={() => void handleRememberPhoto(!rememberPhoto)}
                      aria-busy={isRememberingPhoto}
                      className={`flex min-h-[96px] w-full items-center gap-4 rounded-[22px] border p-4 text-left text-[10px] leading-relaxed transition active:scale-[0.99] ${rememberPhoto ? 'border-emerald-300/60 bg-emerald-400/15 text-emerald-50' : 'border-white/10 bg-white/5 text-white/65'}`}
                    >
                      <span aria-hidden="true" className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border transition ${rememberPhoto ? 'border-emerald-200 bg-emerald-400 text-slate-950' : 'border-white/25 bg-white/5 text-white/70'}`}>
                        {rememberPhoto ? <IconCheck className="h-5 w-5" /> : <span className="h-4 w-4 rounded border-2 border-current" />}
                      </span>
                      <span><strong className="mb-1 block text-sm text-white">{privatePhotoCopy.remember}</strong>{isRememberingPhoto ? privatePhotoCopy.preparing : privatePhotoCopy.description}</span>
                    </button>
                  </div>
                )}

                <button onClick={() => handleStartWeaving(false)} disabled={isPhotoPreparing || Boolean(profile.photoUri && !photoConsent)} className="w-full py-5 bg-white text-slate-950 rounded-[25px] font-black uppercase tracking-[0.3em] shadow-professional active:scale-95 transition-all shrink-0 disabled:cursor-not-allowed disabled:opacity-40">{t('startNow')}</button>
             </div>
          </div>
        );

      case 'STORY_BOOK':
        if (isGenerating) return (
          <div className="h-full min-h-0 overflow-y-auto scroll-container">
            <div className="flex min-h-full flex-col items-center justify-center gap-5 animate-in fade-in px-5 py-8 sm:gap-7 sm:p-10">
              <div className="text-center space-y-3 px-4">
                <h2 className="text-2xl sm:text-3xl handwritten magical-glow">{weavingStep === 'STORY' ? t('weavingStory') : weavingStep === 'PAINTING' ? t('weavingArt') : t('narrationPreparing')}</h2>
                <div className="relative h-1 w-40 overflow-hidden rounded-full bg-white/10"><div className="absolute inset-0 bg-white/60 animate-progress-magic" /></div>
              </div>
              <WeavingCompanion language={profile.language} childName={profile.name} stage={weavingStep} />
            </div>
          </div>
        );

        if (!currentStory || !currentStory.pages) return null;
        const page = currentStory.pages[currentPageIdx];
        const isAtEnd = currentPageIdx === currentStory.pages.length - 1;
        return (
          <div 
            className="story-reader-stage w-full h-full flex flex-col bg-black relative overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
             <div className="absolute inset-0 z-0 flex items-center justify-center overflow-hidden">
                {currentStory.pages.map((p, idx) => (
                  <div
                    key={idx}
                    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${idx === currentPageIdx ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                    style={{ willChange: 'opacity' }}
                  >
                    {p.imageUrl ? (
                      <>
                        <img src={p.imageUrl} className="story-reader-backdrop absolute inset-0 h-full w-full scale-110 object-cover opacity-35 blur-2xl" alt="" aria-hidden="true" />
                        <img src={p.imageUrl} className={`story-page-image relative h-full w-full transition-all duration-1000 ${idx === currentPageIdx ? 'dream-scene-motion' : ''} ${isContinuationLoading ? 'blur-md scale-105' : 'blur-0'}`} alt={`Illustration zu Seite ${idx + 1}`} />
                      </>
                    ) : (
                      <div className="relative w-full h-full overflow-hidden bg-[#11152f]">
                        <img src="/assets/lumi-portal.jpg" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-35 blur-[2px]" alt="" aria-hidden="true" />
                        <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/35 via-slate-950/25 to-slate-950/70" />
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-10 text-center">
                          <div className="h-28 w-28 animate-float-moon opacity-95 drop-shadow-[0_0_45px_rgba(255,255,255,0.6)]"><MemoizedMoon /></div>
                          <div className="max-w-[220px] rounded-2xl border border-white/15 bg-slate-950/35 px-5 py-4 backdrop-blur-xl">
                            <p className="text-base font-bold dream-font text-white">{p.imageFailed ? t('error') : t('weavingArt')}</p>
                            {!p.imageFailed && <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/15"><div className="h-full w-2/5 rounded-full bg-white/75 animate-progress-magic" /></div>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div className={`absolute inset-0 z-20 transition-colors duration-1000 ${isContinuationLoading ? 'bg-indigo-950/40' : readerFocusMode ? 'bg-transparent' : 'bg-black/40'}`} />
             </div>

             {!readerFocusMode && <aside className="story-rotate-hint fixed left-1/2 top-[calc(4.35rem+var(--safe-top))] z-40 -translate-x-1/2 rounded-2xl border border-white/20 bg-slate-950/80 px-3 py-2 text-center shadow-[0_10px_30px_rgba(0,0,0,0.28)] backdrop-blur-xl" aria-live="polite">
               <div className="flex items-center justify-center gap-2 text-[10px] font-black tracking-wide text-white"><IconRotateDevice className="h-4 w-4 text-amber-100" /> {mobileReaderCopy.title}</div>
               <p className="mt-0.5 text-[9px] text-white/70">{mobileReaderCopy.description}</p>
             </aside>}

             {showReaderFocusHint && !isContinuationLoading && !readerFocusMode && (
               <aside className="story-reader-picture-tip fixed top-[calc(4.65rem+var(--safe-top))] z-[70] rounded-2xl border border-cyan-100/25 bg-slate-950/90 px-4 py-3 text-center shadow-[0_12px_36px_rgba(0,0,0,0.38)] backdrop-blur-xl animate-in fade-in slide-in-from-top-2" aria-live="polite">
                 <div className="flex items-center justify-center gap-2"><IconPictureMode className="h-4 w-4 shrink-0 text-cyan-100" /><p className="text-[10px] font-semibold leading-relaxed text-white/90">{readerFocusCopy.tip}</p></div>
               </aside>
             )}

             {isContinuationLoading && (
               <div className="absolute inset-0 z-[60] flex items-center justify-center p-10 animate-in fade-in duration-500">
                  <LumiMessage message={t('weavingContinue')} isThinking={true} />
               </div>
             )}
             
             <header className={`story-reader-header fixed top-0 left-0 right-0 flex items-center justify-between z-50 transition-opacity duration-500 ${isContinuationLoading ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                <button onClick={() => { stopAudio(); setReaderFocusMode(false); setAppState('HOME'); }} aria-label={t('back')} className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/20 bg-black/50 shadow-professional backdrop-blur-xl transition-all active:scale-90 sm:h-12 sm:w-12"><IconBack className="w-4 h-4" /></button>
                <div className={`rounded-full border border-white/20 bg-black/50 px-4 py-1.5 text-[9px] font-black tracking-widest opacity-80 backdrop-blur-xl transition-opacity ${readerFocusMode ? 'pointer-events-none opacity-0' : 'opacity-80'}`}>{currentPageIdx + 1} / {currentStory.pages.length}</div>
                <button onClick={toggleReaderFocusMode} aria-pressed={readerFocusMode} aria-label={readerFocusMode ? readerFocusCopy.hide : readerFocusCopy.show} className={`flex h-11 w-11 items-center justify-center rounded-2xl border shadow-professional backdrop-blur-xl transition-all active:scale-90 sm:h-12 sm:w-12 ${readerFocusMode ? 'border-cyan-100 bg-cyan-100/25 text-cyan-50' : 'border-white/40 bg-black/50 text-white'}`}><IconPictureMode className="h-5 w-5" /></button>
             </header>

             {showLyrics && page && !isContinuationLoading && (
                <div className="story-reader-lyrics mt-auto max-h-[58%] p-5 pb-[calc(7.75rem+var(--safe-bottom))] sm:max-h-[50%] sm:p-8 sm:pb-[calc(140px+var(--safe-bottom))] z-30 text-center animate-in slide-in-from-bottom-5 scroll-container no-scrollbar">
                   <p className="text-base sm:text-xl md:text-3xl font-light leading-[1.45] sm:leading-relaxed dream-font text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)] magical-glow">{page.content}</p>
                </div>
             )}

             {isAtEnd && !isContinuationLoading && !readerFocusMode && (
                <div className={`story-reader-end-actions fixed ${showControls ? 'bottom-[calc(8.75rem+var(--safe-bottom))] sm:bottom-[calc(11rem+var(--safe-bottom))]' : 'bottom-[calc(1.5rem+var(--safe-bottom))]'} left-1/2 z-[80] grid -translate-x-1/2 grid-cols-2 gap-2`}>
                  <button
                    type="button"
                    disabled={paymentProcessing}
                    onClick={() => void handlePrintBookCheckout(currentStory)}
                    className="flex min-h-12 items-center justify-center gap-2 rounded-full border border-amber-100/40 bg-amber-100/15 px-3 text-center text-[8px] font-black uppercase tracking-[0.1em] text-amber-50 shadow-[0_10px_38px_rgba(0,0,0,0.45)] backdrop-blur-2xl transition active:scale-95 disabled:opacity-50 sm:text-[9px]"
                  >
                    <IconBook className="h-4 w-4 shrink-0" /> {paymentProcessing ? t('pleaseWait') : t('printBook')}
                  </button>
                  <button
                    type="button"
                    disabled={isGenerating}
                    onClick={() => handleStartWeaving(true)}
                    className="min-h-12 rounded-full border border-white/45 bg-slate-950/75 px-3 text-center text-[8px] font-black uppercase tracking-[0.1em] text-white shadow-[0_10px_38px_rgba(0,0,0,0.45)] backdrop-blur-2xl transition active:scale-95 disabled:opacity-50 sm:text-[9px]"
                  >
                    {t('continueStory')} ✨
                  </button>
                </div>
             )}

             {showControls && !isContinuationLoading && !readerFocusMode && (
                <footer className="story-reader-footer story-reader-controls fixed bottom-0 left-0 right-0 z-50 flex justify-center animate-in slide-in-from-bottom-5 transition-opacity duration-500">
                   <div className="bg-black/60 backdrop-blur-2xl border border-white/20 p-2.5 sm:p-3 rounded-[30px] sm:rounded-[35px] flex items-center gap-2.5 sm:gap-5 shadow-professional">
                      <button disabled={currentPageIdx === 0} onClick={() => setCurrentPageIdx(idx => idx - 1)} className={`h-11 w-11 sm:w-12 sm:h-12 flex items-center justify-center transition-all ${currentPageIdx === 0 ? 'opacity-0 pointer-events-none' : 'active:scale-75 opacity-80'}`}><IconBack className="w-5 h-5" /></button>
                      <button
                        aria-label={isPlaying ? 'Vorlesen pausieren' : 'Diese Seite vorlesen'}
                        onClick={() => {
                          if (isPlaying) {
                            stopAudio();
                          } else {
                            // This click is a fresh user gesture, which lets
                            // browsers that suspended background audio start
                            // narration reliably after a long image render.
                            primeNarration();
                            void playSpeech(page.content);
                          }
                        }}
                        className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all shadow-professional relative group ${isPlaying ? 'bg-white/30 scale-105 border border-white' : 'bg-white active:scale-90'}`}
                      >
                         {isPlaying && <div className="absolute inset-0 rounded-full bg-white animate-ping opacity-20" />}
                         {isPlaying && <div className="absolute inset-[-20px] rounded-full bg-white/20 blur-xl animate-pulse" />}
                         {isPlaying ? <IconPause className="text-white w-6 h-6 z-10" /> : <IconPlay className="text-slate-950 w-6 h-6 ml-1 z-10" />}
                      </button>
                      <button disabled={isAtEnd} onClick={() => setCurrentPageIdx(idx => idx + 1)} className={`h-11 w-11 sm:w-12 sm:h-12 flex items-center justify-center transition-all ${isAtEnd ? 'opacity-0 pointer-events-none' : 'active:scale-75 opacity-80'}`}><IconBack className="w-5 h-5 rotate-180" /></button>
                   </div>
                </footer>
             )}
          </div>
        );

      case 'LIBRARY':
        return (
          <div className="w-full h-full flex flex-col px-4 sm:px-8 screen-in safe-padding-top scroll-container">
             <header className="flex justify-between items-center py-6 shrink-0">
                <button onClick={() => setAppState('HOME')} className="w-10 h-10 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center active:scale-90 transition-all shadow-professional"><IconBack className="w-4 h-4 opacity-80" /></button>
                <h2 className="text-xl font-bold dream-font">{t('myStories')}</h2>
                <div className="w-10" />
             </header>

             {savedStories.length === 0 ? (
               <div className="flex-1 flex flex-col items-center justify-center text-center gap-8 animate-in fade-in">
                  <div className="w-32 h-32 opacity-40 drop-shadow-[0_0_50px_rgba(255,255,255,0.5)]"><MemoizedMoon /></div>
                  <button onClick={() => setAppState('CREATE_STORY')} className="px-10 py-4 bg-white/10 rounded-full border border-white/20 font-bold uppercase tracking-[0.3em] text-[9px] active:scale-95 transition-all shadow-professional">{t('startStory')}</button>
               </div>
             ) : (
               <>
               <div className="w-full grid grid-cols-1 gap-4 pb-6 mt-4">
                  {savedStories.map((s) => (
                    <article key={s.id} className="overflow-hidden rounded-[30px] border border-white/10 bg-white/5 shadow-professional">
                      <button onClick={() => { setCurrentStory(s); setCurrentPageIdx(0); setAppState('STORY_BOOK'); }} className="flex w-full items-center gap-4 p-4 text-start transition-all active:scale-[0.99]">
                        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[20px] border border-white/20 bg-white/10">
                          {s.pages && s.pages[0]?.imageUrl ? <img src={s.pages[0].imageUrl} className="h-full w-full object-cover" alt={t('titleImage')} /> : <IconBook className="h-6 w-6 opacity-30" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="break-words text-lg font-bold dream-font">{s.title}</h3>
                          <p className="mt-1 text-[8px] uppercase tracking-[0.2em] opacity-30">{new Date(s.createdAt).toLocaleDateString()}</p>
                        </div>
                      </button>
                      <div className="flex flex-col gap-2 border-t border-white/10 p-3 sm:flex-row">
                        <button
                          type="button"
                          aria-label={`${s.title}: ${t('printBook')}`}
                          disabled={paymentProcessing}
                          onClick={() => void handlePrintBookCheckout(s)}
                          className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border border-amber-100/25 bg-amber-200/10 px-4 text-center text-[9px] font-black uppercase tracking-[0.12em] text-amber-50/95 transition active:scale-[0.98] disabled:opacity-50"
                        >
                          <IconBook className="h-4 w-4 shrink-0" /> {paymentProcessing ? t('pleaseWait') : t('printBook')}
                        </button>
                        <button aria-label={`${s.title}: ${t('deleteAccount')}`} onClick={() => deleteFromLibrary(s.id)} className="flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-red-200/10 bg-red-500/10 px-4 text-[9px] font-black uppercase tracking-[0.12em] text-red-100/75 transition active:scale-[0.98]"><IconTrash className="h-4 w-4" /> <span className="sm:hidden">{t('deleteAccount')}</span></button>
                      </div>
                    </article>
                  ))}
               </div>
               {savedStories.length > 0 && <button type="button" onClick={() => setConfirmModal('DELETE_STORIES')} className="w-full rounded-2xl border border-red-300/20 bg-red-500/10 px-5 py-4 text-[9px] font-black uppercase tracking-[0.18em] text-red-100/80">{t('deleteAllStories')}</button>}
               </>
             )}
          </div>
        );

      case 'ACCOUNT':
        return (
          <div className="w-full h-full flex flex-col px-8 screen-in safe-padding-top scroll-container">
             <header className="flex justify-between items-center py-6 shrink-0">
                <button onClick={() => setAppState('HOME')} className="w-10 h-10 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center active:scale-90 transition-all shadow-professional"><IconBack className="w-4 h-4 opacity-80" /></button>
                <h2 className="text-xl font-bold dream-font">{t('account')}</h2>
                <div className="w-10" />
             </header>

             <div className="mt-4 flex flex-col items-center gap-6 shrink-0">
                <div className="relative">
                  <div className="w-24 h-24 rounded-[30px] bg-white/20 p-1 border border-white/40 shadow-professional relative overflow-hidden flex items-center justify-center">
                    {user?.photoURL ? 
                      <img src={user.photoURL} className="w-full h-full object-cover rounded-[29px]" alt="Profile" /> : 
                      <IconUser className="w-8 h-8 opacity-30" />
                    }
                  </div>
                  <input type="file" id="profile-photo-input" className="hidden" accept="image/*" onChange={handleProfilePhotoChange} />
                  <button onClick={() => document.getElementById('profile-photo-input')?.click()} className="absolute -bottom-1 -right-1 w-8 h-8 bg-white text-slate-950 rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all">
                    <IconCamera className="w-4 h-4" />
                  </button>
                  {user?.photoURL && (
                    <button aria-label={t('photoRemove')} onClick={() => void handleRemoveProfilePhoto()} className="absolute -top-1 -left-1 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all">
                      <IconTrash className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="text-center space-y-1">
                   <h3 className="text-3xl font-bold dream-font">{user?.displayName || '...'}</h3>
                   <p className="text-xs text-white/30">{user?.email}</p>
                </div>
             </div>

             <section className="mt-8 w-full rounded-[30px] border border-white/10 bg-white/5 p-6 shadow-professional shrink-0">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10"><IconUser className="h-4 w-4 opacity-70" /></div>
                  <div>
                    <p className="text-xs font-bold">{t('profile')}</p>
                    <p className="text-[9px] leading-relaxed text-white/45">Dieser Name erscheint in den persönlichen Geschichten.</p>
                  </div>
                </div>
                <label className="block">
                  <span className="mb-2 block text-[8px] font-black uppercase tracking-[0.22em] text-white/35">{t('onboardingName')}</span>
                  <input
                    value={profile.name}
                    onChange={(event) => updateChildName(event.target.value)}
                    onBlur={() => void persistChildProfile()}
                    maxLength={30}
                    className="lumi-input w-full py-3 text-lg font-bold dream-font"
                    placeholder={t('onboardingName')}
                    aria-label={t('onboardingName')}
                  />
                </label>
              </section>

             <div className="mt-10 w-full bg-white/5 border border-white/10 rounded-[35px] p-8 space-y-6 shadow-professional relative overflow-hidden shrink-0">
                <div className="flex justify-between items-center relative z-10">
                   <div className="space-y-1">
                      <p className="text-[8px] uppercase font-black tracking-[0.4em] text-white/20">{t('premiumActive')}</p>
                      <p className={`text-xl font-bold ${isPremium ? 'magical-glow' : 'text-white'}`}>{isPremium ? 'Lumi Premium' : 'Lumi Start'}</p>
                   </div>
                   {!isPremium ? <button onClick={() => setAppState('SUBSCRIPTION')} className="px-5 py-2.5 bg-white text-slate-950 rounded-xl font-bold text-[9px] uppercase tracking-wider shadow-professional">{t('upgrade')}</button> : <button onClick={() => void handleBillingPortal()} className="px-4 py-2.5 bg-white/10 border border-white/15 rounded-xl font-bold text-[9px] uppercase tracking-wider">{t('manageSubscription')}</button>}
                </div>
                <div className="h-px bg-white/10 relative z-10" />
                <div className="grid grid-cols-2 gap-3 relative z-10">
                   <div className="space-y-1">
                      <p className="text-[7px] uppercase font-black tracking-[0.3em] text-white/20">{t('myStories')}</p>
                      <p className="text-lg font-bold">{savedStories.length}</p>
                   </div>
                   <div className="space-y-1">
                      <p className="text-[7px] uppercase font-black tracking-[0.3em] text-white/20">{isPremium ? t('credits') : t('testStories')}</p>
                      <p className="text-lg font-bold">{isPremium ? '∞' : `${Math.max(0, usesRemaining)} / ${user?.trialStoryLimit || 3}`}</p>
                   </div>
                </div>
             </div>

             <div className="mt-8 space-y-3 pb-24 shrink-0">
                {user && !user.emailVerified && !user.ownerAccess && (
                  <section className="rounded-[25px] border border-amber-200/30 bg-amber-100/10 p-5 shadow-professional" aria-live="polite">
                    <div className="flex gap-3">
                      <IconShield className="mt-0.5 h-5 w-5 shrink-0 text-amber-100" />
                      <div>
                        <h3 className="text-xs font-bold text-amber-50">{t('emailVerificationTitle')}</h3>
                        <p className="mt-1 text-[10px] leading-relaxed text-amber-50/75">{t('emailVerificationText')}</p>
                        <button type="button" onClick={() => void handleResendVerification()} disabled={resendingVerification} className="mt-4 rounded-xl border border-amber-100/40 bg-amber-50/10 px-4 py-2 text-[9px] font-black uppercase tracking-[0.16em] text-amber-50 disabled:opacity-50">
                          {resendingVerification ? t('sending') : t('resendEmail')}
                        </button>
                      </div>
                    </div>
                  </section>
                )}
                <section className="rounded-[25px] border border-white/10 bg-white/5 p-5 shadow-professional">
                  <label className="flex cursor-pointer items-start justify-between gap-4">
                    <span>
                      <strong className="block text-xs">{MARKETING_CONSENT_COPY[profile.language].title}</strong>
                      <span className="mt-1 block text-[10px] leading-relaxed text-white/50">{MARKETING_CONSENT_COPY[profile.language].description}</span>
                    </span>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={Boolean(user?.marketingConsent)}
                      onChange={(event) => void handleMarketingConsentChange(event.target.checked)}
                      className="mt-1 h-5 w-5 shrink-0 accent-amber-300"
                    />
                  </label>
                </section>
                <button onClick={() => { stopAudio(); setShowSettings(true); }} className="w-full p-5 bg-white/5 border border-white/10 rounded-[25px] flex items-center justify-between active:bg-white/10 transition-colors shadow-professional">
                   <div className="flex items-center gap-4"><IconSettings className="w-4 h-4 opacity-60" /><span className="text-xs font-bold">{t('settings')}</span></div>
                   <IconBack className="w-3 h-3 rotate-180 opacity-20" />
                </button>
                <button aria-expanded={showPasswordChange} onClick={() => { setShowPasswordChange((open) => !open); setPasswordChangeError(''); }} className="w-full p-5 bg-white/5 border border-white/10 rounded-[25px] flex items-center justify-between active:bg-white/10 transition-colors shadow-professional">
                   <div className="flex items-center gap-4"><IconShield className="w-4 h-4 opacity-60" /><span className="text-xs font-bold">{t('accountSecurity')}</span></div>
                   <IconBack className={`w-3 h-3 opacity-20 transition-transform ${showPasswordChange ? '-rotate-90' : 'rotate-180'}`} />
                </button>
                {showPasswordChange && (
                  <form onSubmit={handlePasswordChange} className="rounded-[25px] border border-indigo-200/15 bg-indigo-400/10 p-5 space-y-4 animate-in fade-in slide-in-from-top-2">
                    <h3 className="text-sm font-bold dream-font">{t('changePassword')}</h3>
                    <p className="text-[10px] leading-relaxed text-white/55">{t('changePasswordText')}</p>
                    <label className="block"><span className="mb-2 block text-[8px] font-black uppercase tracking-[0.22em] text-white/35">{t('currentPassword')}</span><input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" required className="lumi-input w-full text-sm py-3" /></label>
                    <label className="block"><span className="mb-2 block text-[8px] font-black uppercase tracking-[0.22em] text-white/35">{t('newPassword')}</span><input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" required minLength={10} className="lumi-input w-full text-sm py-3" /></label>
                    {passwordChangeError && <p className="text-[10px] text-red-200">{passwordChangeError}</p>}
                    <button disabled={passwordChangeSubmitting} className="w-full rounded-2xl bg-white py-3 text-[9px] font-black uppercase tracking-[0.2em] text-slate-950 disabled:opacity-50">{passwordChangeSubmitting ? t('pleaseWait') : t('securePasswordChange')}</button>
                  </form>
                )}
                <button aria-expanded={showSupport} onClick={() => { setShowSupport((open) => !open); setSupportError(''); }} className="w-full p-5 bg-white/5 border border-white/10 rounded-[25px] flex items-center justify-between active:bg-white/10 transition-colors shadow-professional">
                   <div className="flex items-center gap-4"><IconAlert className="w-4 h-4 opacity-60" /><span className="text-xs font-bold">{t('helpSupport')}</span></div>
                   <IconBack className={`w-3 h-3 opacity-20 transition-transform ${showSupport ? '-rotate-90' : 'rotate-180'}`} />
                </button>
                {showSupport && (
                  <form onSubmit={handleSupportSubmit} className="rounded-[25px] border border-indigo-200/15 bg-indigo-400/10 p-5 space-y-4 animate-in fade-in slide-in-from-top-2">
                    <div>
                      <h3 className="text-sm font-bold dream-font">{t('supportTitle')}</h3>
                      <p className="mt-1 text-[10px] leading-relaxed text-white/55">{t('supportText')}</p>
                    </div>
                    <label className="block"><span className="mb-2 block text-[8px] font-black uppercase tracking-[0.22em] text-white/35">{t('subject')}</span><input value={supportSubject} onChange={(event) => setSupportSubject(event.target.value)} required minLength={3} maxLength={120} className="lumi-input w-full text-sm py-3" placeholder={t('supportSubjectHint')} /></label>
                    <label className="block"><span className="mb-2 block text-[8px] font-black uppercase tracking-[0.22em] text-white/35">{t('message')}</span><textarea value={supportMessage} onChange={(event) => setSupportMessage(event.target.value)} required minLength={10} maxLength={3000} rows={5} className="lumi-input w-full resize-y text-sm py-3" placeholder={t('supportMessageHint')} /></label>
                    {supportError && <p role="alert" className="text-[10px] text-red-200">{supportError}</p>}
                    <button disabled={supportSubmitting} className="w-full rounded-2xl bg-white py-3 text-[9px] font-black uppercase tracking-[0.2em] text-slate-950 disabled:opacity-50">{supportSubmitting ? t('sending') : t('sendMessage')}</button>
                  </form>
                )}
                <button onClick={() => setConfirmModal('LOGOUT')} className="w-full py-4 text-white/40 font-black uppercase tracking-[0.3em] text-[9px] active:text-white/60">{t('logout')}</button>
                <button onClick={() => setConfirmModal('DELETE')} className="w-full py-2 text-white/10 font-bold text-[8px] uppercase tracking-[0.2em] active:text-red-500/40">{t('deleteAccount')}</button>
             </div>
          </div>
        );

      case 'SUBSCRIPTION':
        return (
          <div className="w-full h-full flex flex-col px-8 screen-in safe-padding-top scroll-container">
             <header className="flex items-center gap-4 py-6 shrink-0">
                <button onClick={() => setAppState('HOME')} className="w-10 h-10 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center active:scale-90 transition-all shadow-professional"><IconBack className="w-4 h-4 opacity-80" /></button>
                <h2 className="text-xl font-bold dream-font">{t('upgrade')}</h2>
             </header>

             <div className="flex-1 flex flex-col items-center justify-center text-center gap-8 py-8 shrink-0">
                <div className="w-32 h-32 bg-white/10 border border-white/40 rounded-[40px] flex items-center justify-center shadow-[0_0_80px_rgba(255,255,255,0.4)] animate-pulse">
                   <IconStar className="w-16 h-16 text-white" />
                </div>
                <div className="space-y-3 px-4">
                   <h3 className="text-3xl font-bold dream-font magical-glow">{t('unlimitedMagic')}</h3>
                   <p className="text-xs text-white/50 leading-relaxed">{t('unlimitedDesc')}</p>
                </div>
             </div>

             <div className="w-full space-y-3 pb-24 shrink-0">
                {isPremium ? (
                  <button onClick={() => void handleBillingPortal()} className="w-full py-5 bg-white text-slate-950 rounded-[30px] font-black uppercase tracking-[0.2em] shadow-professional">{t('manageSubscription')}</button>
                ) : (
                  <>
                    <button onClick={() => void handleUpgrade('monthly')} disabled={paymentProcessing} className="w-full py-5 bg-white text-slate-950 rounded-[30px] font-black uppercase tracking-[0.15em] shadow-professional active:scale-95 transition-all flex items-center justify-center gap-4 disabled:opacity-50">
                      <IconCreditCard className="w-4 h-4" /> {t('monthly')}
                    </button>
                    <button onClick={() => void handleUpgrade('yearly')} disabled={paymentProcessing} className="w-full py-5 bg-indigo-400/15 border border-indigo-200/25 text-white rounded-[30px] font-black uppercase tracking-[0.15em] shadow-professional active:scale-95 transition-all">{t('yearly')}</button>
                  </>
                )}
                <p className="text-[9px] text-white/30 leading-relaxed text-center">{t('stripeInfo')}</p>
             </div>
          </div>
        );

      default: return null;
    }
  };

  return (
    <StarryBackground>
      {isPrivacyShieldActive && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/80 backdrop-blur-[50px] flex flex-col items-center justify-center animate-in fade-in">
            <div className="w-24 h-24 mb-6 opacity-60"><MemoizedMoon /></div>
        </div>
      )}

      {renderScreen()}
      
      {safetyAlarm && (
        <div className="fixed inset-0 z-[9000] bg-slate-950/98 backdrop-blur-3xl flex items-center justify-center p-8 animate-in fade-in">
           <div className="w-full max-w-xs bg-white/5 border border-white/20 rounded-[35px] p-8 text-center space-y-8 shadow-professional">
              <div className="w-16 h-16 bg-indigo-500/10 text-indigo-400 rounded-full mx-auto flex items-center justify-center shadow-[0_0_40px_rgba(129,140,248,0.2)] animate-pulse">
                <IconShield className="w-8 h-8" />
              </div>
              <div className="space-y-3">
                <h2 className="text-2xl font-bold dream-font">{t('guardTitle')}</h2>
                <p className="text-xs text-white/50 leading-relaxed font-medium">{t('guardDesc')}</p>
              </div>
              <div className="space-y-3">
                <button onClick={() => setSafetyAlarm(false)} className="w-full py-4 bg-white text-slate-950 rounded-[20px] font-black uppercase tracking-widest shadow-professional active:scale-95 transition-all">{t('understood')}</button>
              </div>
           </div>
        </div>
      )}

      {paymentProcessing && (
        <div className="fixed inset-0 z-[900] bg-slate-950/60 backdrop-blur-3xl flex items-center justify-center p-10 animate-in fade-in">
           <div className="flex flex-col items-center gap-6 text-center">
              <div className="w-20 h-20 border-4 border-white/20 border-t-white animate-spin rounded-full shadow-[0_0_40px_rgba(255,255,255,0.3)]" />
              <p className="text-xl handwritten magical-glow">{t('connecting')}</p>
           </div>
        </div>
      )}

      {confirmModal && (
        <div className="fixed inset-0 z-[800] bg-slate-950/90 backdrop-blur-2xl flex items-center justify-center p-8 animate-in fade-in">
           <div className="w-full max-w-xs bg-white/5 border border-white/20 rounded-[35px] p-8 text-center space-y-6 shadow-professional">
              <div className="space-y-2">
                 <h2 className="text-2xl font-bold dream-font">{confirmModal === 'DELETE_STORIES' ? t('deleteAllStoriesTitle') : confirmModal === 'DELETE' ? t('deleteConfirm') : t('logoutConfirm')}</h2>
                 <p className="text-xs text-white/40 leading-relaxed">
                   {confirmModal === 'DELETE_STORIES' ? t('deleteAllStoriesText') : confirmModal === 'DELETE' ? t('deleteDesc') : t('logoutDesc')}
                 </p>
              </div>
              <div className="space-y-2">
                 <button onClick={confirmModal === 'DELETE_STORIES' ? deleteAllStories : confirmModal === 'DELETE' ? handleDeleteAccount : handleLogout} className={`w-full py-4 rounded-[20px] font-black uppercase tracking-[0.2em] shadow-professional active:scale-95 transition-all ${confirmModal === 'DELETE' || confirmModal === 'DELETE_STORIES' ? 'bg-red-500 text-white' : 'bg-white text-slate-950'}`}>
                    {confirmModal === 'DELETE_STORIES' ? t('deleteAllStories') : confirmModal === 'DELETE' ? t('yesDelete') : t('yesLogout')}
                 </button>
                 <button onClick={() => setConfirmModal(null)} className="w-full py-2 text-white/30 text-[9px] font-bold uppercase tracking-[0.3em]">{t('cancel')}</button>
              </div>
           </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-3xl p-8 flex flex-col screen-in safe-padding-top safe-padding-bottom">
           <header className="flex justify-between items-center mb-10 shrink-0">
              <div>
                <h2 className="text-3xl handwritten magical-glow mb-1">{t('settings')}</h2>
              </div>
              <button onClick={() => setShowSettings(false)} className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-3xl active:scale-90 transition-all shadow-professional">×</button>
           </header>
           
           <div className="space-y-8 max-w-sm mx-auto w-full flex-1 scroll-container pb-10 no-scrollbar">
              <section className="bg-white/5 p-6 rounded-[30px] border border-white/10 shadow-professional">
                <p className="text-[9px] font-black uppercase tracking-[0.4em] text-white/20 mb-6 text-center">{t('language')}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {LANGUAGES.map(l => (
                    <button key={l.code} onClick={() => { const nextProfile = {...profile, language: l.code as LanguageCode}; setProfile(nextProfile); saveProfilePreferences(nextProfile); }} className={`aspect-square rounded-xl border transition-all flex flex-col items-center justify-center gap-1 ${profile.language === l.code ? 'bg-white/20 border-white scale-105 shadow-lg' : 'bg-white/5 border-transparent opacity-30'}`}>
                      <span className="text-xl">{l.flag}</span>
                      <span className="text-[7px] font-bold">{l.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="bg-white/5 p-6 rounded-[30px] border border-white/10 shadow-professional">
                <p className="text-[9px] font-black uppercase tracking-[0.4em] text-white/20 mb-6 text-center">{t('voice')}</p>
                <p className="-mt-3 mb-5 text-center text-[9px] leading-relaxed text-white/45">{t('voiceHint')}</p>
                <div className="grid grid-cols-3 gap-3">
                  {VOICES.map(v => (
                    <button key={v.id} type="button" aria-pressed={profile.voice === v.id} aria-label={`${v.name}: ${t('voiceAria')}`} onClick={() => playVoicePreview(v.id)} className={`aspect-square rounded-xl border transition-all flex flex-col items-center justify-center gap-1 ${profile.voice === v.id ? 'bg-white/20 border-white scale-105 shadow-lg' : 'bg-white/5 border-transparent opacity-50'}`}>
                      <span className="text-[10px] font-bold dream-font">{v.name}</span>
                      <span className="text-[7px] uppercase tracking-wide text-white/60">{previewVoice === v.id ? t('voicePreview') : t(v.descKey)}</span>
                    </button>
                  ))}
                </div>
              </section>
           </div>
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-[calc(2rem+var(--safe-area-bottom))] left-8 right-8 p-4 rounded-[20px] border backdrop-blur-3xl z-[300] animate-in slide-in-from-bottom-5 fade-in duration-500 shadow-professional flex items-center gap-3 ${toast.type === 'success' ? 'bg-white/10 border-white/30' : 'bg-red-500/20 border-red-500/30'}`}>
           <p className="text-[11px] font-bold dream-font">{toast.message}</p>
        </div>
      )}

      {appState === 'STORY_BOOK' && isNarrationPreparing && !readerFocusMode && (
        <div aria-live="polite" className="fixed bottom-[calc(7.8rem+var(--safe-area-bottom))] left-1/2 z-[210] -translate-x-1/2 rounded-full border border-white/15 bg-slate-950/75 px-4 py-2 text-[9px] font-bold tracking-wide text-white/80 shadow-professional backdrop-blur-xl">
          {t('narrationPreparing')}
        </div>
      )}
    </StarryBackground>
  );
};

const AppRouter: React.FC = () => {
  // The administration area deliberately has no link in the family app. It
  // stays on the same trusted domain, then the server checks the signed
  // session and the owner flag before it returns any metrics.
  const isAdminRoute = typeof window !== 'undefined' && /^\/admin\/?$/.test(window.location.pathname);
  return isAdminRoute ? <AdminDashboard /> : <LumiApp />;
};

const App: React.FC = () => (
  <AuthProvider>
    <SubscriptionProvider>
      <AppRouter />
    </SubscriptionProvider>
  </AuthProvider>
);

export default App;
